// src/components/MarketShareView.jsx
//
// Phase I — Market share tool. Three stacked tables that all read from
// the same market_universe view + competitor_homes table.
//
//   1. By LFG Location — share inside each location's county
//   2. By Territory   — aggregate share across each territory's state set
//   3. By County      — sortable / state-filterable drill-down
//
// Empty-state messaging when competitor seed or county population data
// hasn't been loaded yet so the tool is still useful as soon as it ships.

import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Building2, MapPin, Users, BarChart3, Loader2, AlertTriangle, Filter, Plus, X, Sparkles, ChevronRight, ChevronDown, Map as MapIcon, TrendingUp, Search, GitCompare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTheme } from '../lib/ThemeContext';
import { applyAll as applyDarkOverrides } from '../lib/darkModeOverrides';
import { pointInPolygon } from './TerritoryMapEditor';
import { US_COUNTY_CENTROIDS } from '../data/usCountyCentroids';

// US average annual death rate (deaths per person per year). Multiplied by
// population to estimate "addressable annual funerals" in a geography.
const DEATH_RATE = 0.0085;

// Haversine distance in miles between two lat/lng points.
function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) ** 2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// The 4 core LFG market regions (also used on the Map view as radius
// circles). Each carries the list of counties that fall inside the
// radius — used by the thorough sweep to decompose discovery into
// per-county passes, and by the scan-status panel to define the
// canonical "tracked" county set.
const HARDCODED_RADIUS_REGIONS = [
  {
    id: 'toledo', name: 'Toledo, OH', center: 'Toledo, OH',
    lat: 41.6528, lng: -83.5379, radiusMiles: 50,
    counties: [
      { fips: '39095', name: 'Lucas',     state: 'OH' },
      { fips: '39173', name: 'Wood',      state: 'OH' },
      { fips: '39143', name: 'Sandusky',  state: 'OH' },
      { fips: '39123', name: 'Ottawa',    state: 'OH' },
      { fips: '39069', name: 'Henry',     state: 'OH' },
      { fips: '39051', name: 'Fulton',    state: 'OH' },
      { fips: '39039', name: 'Defiance',  state: 'OH' },
      { fips: '26115', name: 'Monroe',    state: 'MI' },
      { fips: '26091', name: 'Lenawee',   state: 'MI' },
    ],
  },
  {
    id: 'akron-canton', name: 'Akron / Canton, OH', center: 'Akron, OH',
    lat: 40.9987, lng: -81.4428, radiusMiles: 30,
    counties: [
      { fips: '39153', name: 'Summit',    state: 'OH' },
      { fips: '39151', name: 'Stark',     state: 'OH' },
      { fips: '39133', name: 'Portage',   state: 'OH' },
      { fips: '39103', name: 'Medina',    state: 'OH' },
      { fips: '39169', name: 'Wayne',     state: 'OH' },
    ],
  },
  {
    id: 'pittsburgh', name: 'Pittsburgh, PA', center: 'Pittsburgh, PA',
    lat: 40.4406, lng: -79.9959, radiusMiles: 25,
    counties: [
      { fips: '42003', name: 'Allegheny',     state: 'PA' },
      { fips: '42129', name: 'Westmoreland',  state: 'PA' },
      { fips: '42125', name: 'Washington',    state: 'PA' },
      { fips: '42007', name: 'Beaver',        state: 'PA' },
      { fips: '42019', name: 'Butler',        state: 'PA' },
    ],
  },
  {
    id: 'cleveland', name: 'Cleveland, OH', center: 'Cleveland, OH',
    lat: 41.4993, lng: -81.6944, radiusMiles: 30,
    counties: [
      { fips: '39035', name: 'Cuyahoga',  state: 'OH' },
      { fips: '39085', name: 'Lake',      state: 'OH' },
      { fips: '39055', name: 'Geauga',    state: 'OH' },
      { fips: '39093', name: 'Lorain',    state: 'OH' },
      { fips: '39103', name: 'Medina',    state: 'OH' },
      { fips: '39153', name: 'Summit',    state: 'OH' },
      { fips: '39007', name: 'Ashtabula', state: 'OH' },
    ],
  },
];

function SectionCard({ title, count, icon: Icon, accent = '#1A3665', children, action }) {
  return (
    <section
      className="rounded-2xl mb-6 overflow-hidden"
      style={{
        backgroundColor: '#fff',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
      }}
    >
      <div className="px-6 py-4 flex items-center justify-between gap-3 flex-wrap" style={{ borderBottom: '1px solid #F0F2F6' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 100%)`, boxShadow: `0 2px 6px ${accent}33` }}
          >
            {Icon && <Icon className="w-4 h-4 text-white" strokeWidth={2.2} />}
          </div>
          <div>
            <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>{title}</h2>
            {typeof count === 'number' && (
              <div className="text-xs tabular-nums mt-0.5" style={{ color: '#64748B' }}>
                {count} row{count === 1 ? '' : 's'}
              </div>
            )}
          </div>
        </div>
        {action}
      </div>
      <div>{children}</div>
    </section>
  );
}

function SharePill({ pct }) {
  if (pct == null) return <span className="text-[11px]" style={{ color: '#94A3B8' }}>—</span>;
  let bg = '#F1F5F9', fg = '#475569';
  if (pct >= 50) { bg = '#DCFCE7'; fg = '#15803D'; }
  else if (pct >= 25) { bg = '#FEF3C7'; fg = '#A16207'; }
  else if (pct > 0)  { bg = '#FEE2E2'; fg = '#B91C1C'; }
  return (
    <span
      className="inline-block text-[11px] font-semibold tabular-nums px-2 py-0.5 rounded-md"
      style={{ backgroundColor: bg, color: fg }}
    >
      {pct.toFixed(1)}%
    </span>
  );
}

// Inline-editable cell for any home's annual call count. Works for both
// LFG locations (lfg_locations.estimated_annual_calls) and competitor
// homes (competitor_homes.estimated_annual_calls) — pass `table` and the
// row id to control which is updated.
function CallsEditCell({ value, rowId, table = 'lfg_locations', isAdmin, onSaved, size = 'md' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setVal(value ?? ''); }, [value]);

  if (!isAdmin || !editing) {
    return (
      <span
        onClick={() => isAdmin && setEditing(true)}
        className={isAdmin ? 'cursor-pointer hover:underline' : ''}
        style={{ color: value != null ? '#0F172A' : '#94A3B8' }}
        title={isAdmin ? 'Click to edit' : undefined}
      >
        {value != null ? Number(value).toLocaleString() : '—'}
      </span>
    );
  }

  const save = async () => {
    setSaving(true);
    const parsed = val === '' ? null : parseInt(val, 10);
    const { error } = await supabase
      .from(table)
      .update({ estimated_annual_calls: Number.isFinite(parsed) ? parsed : null })
      .eq('id', rowId);
    setSaving(false);
    setEditing(false);
    if (!error) onSaved();
  };

  return (
    <input
      type="number"
      autoFocus
      value={val}
      onChange={e => setVal(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') save();
        if (e.key === 'Escape') { setEditing(false); setVal(value ?? ''); }
      }}
      onBlur={save}
      disabled={saving}
      className={`${size === 'sm' ? 'w-16 text-[11px]' : 'w-20 text-[12px]'} text-right tabular-nums border rounded px-1 py-0.5 focus:outline-none focus:ring-1`}
      style={{ borderColor: '#1A3665' }}
    />
  );
}

export default function MarketShareView({ organization, signOut, pc, sc, isAdmin }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [byLocation, setByLocation] = useState([]);
  const [byCounty, setByCounty] = useState([]);
  const [territoriesData, setTerritoriesData] = useState([]);
  const [allLfgLocations, setAllLfgLocations] = useState([]);
  const [allCompetitorHomes, setAllCompetitorHomes] = useState([]);
  const [countyPopulations, setCountyPopulations] = useState({}); // fips -> population
  const [stateFilter, setStateFilter] = useState('');
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [showLocationsList, setShowLocationsList] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showDiscover, setShowDiscover] = useState(false);
  const [capturingSnapshot, setCapturingSnapshot] = useState(false);
  const [snapshotMsg, setSnapshotMsg] = useState(null);
  // Trend panel state — which scope's history to show
  const [trendScope, setTrendScope] = useState({ type: 'overall', value: null, label: 'Overall' });
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendMetric, setTrendMetric] = useState('last_12mo'); // 'ytd' | 'last_12mo' | 'homes'
  // Obit-based methodology (Phase L) — DEPRECATED but kept for comparison.
  // Phase M switched to a deaths-based share model.
  const [obitRows, setObitRows] = useState([]);
  const [obitMetric, setObitMetric] = useState('last_12mo'); // 'last_12mo' | 'ytd'
  const [obitScope, setObitScope] = useState({ type: 'overall', value: null, label: 'Overall' });
  // Scrape health summary (get_obit_scrape_health) — surfaces stuck/AI-
  // discovered/platform-cap counts in-app instead of only in the weekly
  // digest email, so an admin looking at this page right now doesn't have
  // to wait for Monday to know something's wrong.
  const [scrapeHealth, setScrapeHealth] = useState(null);

  // Phase M — Deaths-based market share. For each county where LFG operates,
  // share = sum(lfg_chapel_estimated_annual_calls) / county_annual_deaths
  // (Census Bureau Vintage 2025, sourced from NCHS). Cross-county service
  // moves are treated as negligible — they roughly offset at portfolio scale.
  const [countyShare, setCountyShare] = useState([]);
  const [countyShareTrend, setCountyShareTrend] = useState([]);

  const { darkMode } = useTheme();
  // The global dark-mode DOM walker (darkModeOverrides.js) only re-patches
  // freshly-mounted content on the NEXT animation frame, via its
  // MutationObserver — fine for small, infrequent DOM changes, but this
  // page's loading→loaded transition mounts four stacked panels' worth of
  // elements all at once, so that one-frame lag was a real, visible flash
  // of light colors on every load/refresh. Applying synchronously in
  // useLayoutEffect (which runs after DOM mutations but BEFORE the browser
  // paints) closes that gap for this page specifically, without touching
  // the shared walker other pages still rely on.
  useLayoutEffect(() => {
    if (darkMode) applyDarkOverrides(document.body);
  });

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [locRes, cntRes, terRes, lfgRes, compRes, popRes, obitsRes, shareRes, shareTrendRes, healthRes] = await Promise.all([
        supabase.rpc('get_market_share_by_lfg_location'),
        supabase.rpc('get_market_share_by_county', { p_state: stateFilter || null }),
        supabase.from('territories')
          .select('id, name, color, states, county_fips, drawn_geometry, is_active')
          .eq('organization_id', organization?.id)
          .eq('is_active', true)
          .order('name'),
        supabase.from('lfg_locations')
          .select('id, name, city, state, county_fips, county_name, latitude, longitude, estimated_annual_calls')
          .eq('organization_id', organization?.id)
          .eq('is_active', true),
        supabase.from('competitor_homes')
          .select('id, name, city, state, county_fips, latitude, longitude, estimated_annual_calls, parent_company, is_consolidator, call_count_estimation_status')
          .eq('organization_id', organization?.id)
          .eq('is_active', true),
        supabase.from('county_populations').select('county_fips, population'),
        // Phase L: obit-scraped numbers — kept for back-compat but no longer
        // the primary signal.
        supabase.from('market_homes_obits')
          .select('id, name, city, state, county_fips, latitude, longitude, is_lfg, is_consolidator, parent_company, reference_annual, obits_ytd_attributed, obits_last_12mo_attributed, sitemap_obits_ytd, sitemap_obits_last_12mo, obits_ytd, obits_last_12mo, last_12mo_estimated, data_unreliable, sibling_count, obit_count_estimation_status, obit_count_last_estimated_at, notes')
          .eq('organization_id', organization?.id),
        // Phase M: deaths-based market share — the authoritative signal now.
        supabase.from('market_share_by_county')
          .select('county_fips, county_name, state, chapel_count, lfg_calls, avg_ticket, projected_revenue, dominant_tier, county_deaths, deaths_year, source_label, period_note, lfg_share_pct')
          .eq('organization_id', organization?.id),
        supabase.from('market_share_by_county_trend')
          .select('county_fips, county_name, state, lfg_calls, deaths_year, county_deaths, lfg_share_pct')
          .eq('organization_id', organization?.id)
          .order('deaths_year'),
        supabase.rpc('get_obit_scrape_health', { p_org_id: organization?.id }),
      ]);
      if (locRes.error) throw locRes.error;
      if (cntRes.error) throw cntRes.error;
      if (terRes.error) throw terRes.error;
      if (lfgRes.error) throw lfgRes.error;
      if (compRes.error) throw compRes.error;
      if (popRes.error) throw popRes.error;
      if (obitsRes.error) console.warn('obits view fetch failed:', obitsRes.error.message);
      if (shareRes.error) console.warn('market_share_by_county fetch failed:', shareRes.error.message);
      if (shareTrendRes.error) console.warn('market_share_by_county_trend fetch failed:', shareTrendRes.error.message);
      if (healthRes.error) console.warn('get_obit_scrape_health fetch failed:', healthRes.error.message);
      setByLocation(locRes.data || []);
      setByCounty(cntRes.data || []);
      setTerritoriesData(terRes.data || []);
      setAllLfgLocations(lfgRes.data || []);
      setAllCompetitorHomes(compRes.data || []);
      setScrapeHealth(healthRes.data || null);
      setObitRows(obitsRes.data || []);
      setCountyShare(shareRes.data || []);
      setCountyShareTrend(shareTrendRes.data || []);
      const popMap = {};
      (popRes.data || []).forEach(r => { popMap[r.county_fips] = r.population; });
      setCountyPopulations(popMap);

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [stateFilter, organization?.id]);

  const captureSnapshot = async () => {
    if (capturingSnapshot || !organization?.id) return;
    setCapturingSnapshot(true);
    setSnapshotMsg(null);
    try {
      const { data, error } = await supabase.rpc('capture_market_share_snapshots', { p_org_id: organization.id });
      if (error) throw error;
      setSnapshotMsg({ kind: 'ok', text: `Captured ${data || 0} snapshot(s)` });
      // Refresh the trend panel so the new point shows immediately
      await loadTrend(trendScope);
    } catch (e) {
      console.error('Snapshot capture failed:', e);
      setSnapshotMsg({ kind: 'err', text: e.message || 'Capture failed' });
    } finally {
      setCapturingSnapshot(false);
      setTimeout(() => setSnapshotMsg(null), 4000);
    }
  };

  const loadTrend = async (scope) => {
    if (!organization?.id) return;
    setTrendLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_market_share_obit_trend', {
        p_org_id: organization.id,
        p_scope_type: scope.type,
        p_scope_value: scope.value,
        p_limit: 24,
      });
      if (error) throw error;
      setTrendData(data || []);
    } catch (e) {
      console.error('Trend load failed:', e);
      setTrendData([]);
    } finally {
      setTrendLoading(false);
    }
  };

  useEffect(() => { loadTrend(trendScope); /* eslint-disable-next-line */ }, [organization?.id, trendScope.type, trendScope.value]);

  const toggleRegion = (id) => {
    setExpandedRegions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Region scope-membership test. Counties are no longer the atomic unit —
  // each home (LFG location or competitor) is tested directly against the
  // region's geometry via lat/lng. Falls back to county_fips matching when
  // a home lacks coords (some Claude-discovered competitors don't geocode).
  const homeInRegion = (home, region) => {
    const lat = home.latitude != null ? Number(home.latitude) : null;
    const lng = home.longitude != null ? Number(home.longitude) : null;
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
    const shape = region.shape;
    if (shape.kind === 'circle') {
      if (!hasCoords) return false;
      return haversineMiles(shape.lat, shape.lng, lat, lng) <= shape.miles;
    }
    if (shape.kind === 'polygon') {
      if (hasCoords) return pointInPolygon(lat, lng, shape.polygon);
      // Fallback for not-yet-geocoded homes: match by county_fips against
      // the polygon's auto-derived county set.
      if (shape.fipsSet && home.county_fips) return shape.fipsSet.has(home.county_fips);
      return false;
    }
    if (shape.kind === 'states') {
      return shape.stateSet.has(home.state);
    }
    if (shape.kind === 'counties') {
      return home.county_fips && shape.fipsSet.has(home.county_fips);
    }
    return false;
  };

  // Combined region list: 4 core LFG regions (radius around city)
  // + every active user-defined territory. Home-level aggregation —
  // each LFG/competitor home is tested directly against the region's
  // geometry. No county intermediary.
  const regions = useMemo(() => {
    const sources = [
      ...HARDCODED_RADIUS_REGIONS.map(r => ({
        id: 'core:' + r.id,
        name: r.name,
        source: 'core',
        states: [...new Set(r.counties.map(c => c.state))],
        shape: { kind: 'circle', lat: r.lat, lng: r.lng, miles: r.radiusMiles },
        scopeLabel: `${r.radiusMiles}-mi radius around ${r.center}`,
      })),
      ...territoriesData.map(t => {
        const fipsArr = t.county_fips || [];
        const stateArr = t.states || [];
        const hasPoly = !!t.drawn_geometry;
        let shape, scopeLabel;
        if (hasPoly) {
          // Polygon shape carries the auto-derived county_fips as a fallback
          // for homes that don't have lat/lng yet (e.g. just discovered,
          // not geocoded). Polygon takes precedence when coords exist.
          shape = { kind: 'polygon', polygon: t.drawn_geometry, fipsSet: new Set(fipsArr) };
          scopeLabel = `Polygon · ${fipsArr.length} counties`;
        } else if (fipsArr.length > 0) {
          shape = { kind: 'counties', fipsSet: new Set(fipsArr) };
          scopeLabel = `${fipsArr.length} counties`;
        } else if (stateArr.length > 0) {
          shape = { kind: 'states', stateSet: new Set(stateArr) };
          scopeLabel = `States: ${stateArr.join(', ')}`;
        } else {
          shape = { kind: 'empty' };
          scopeLabel = 'No scope set — draw a polygon on the map';
        }
        return {
          id: 'territory:' + t.id,
          name: t.name,
          source: 'custom',
          color: t.color,
          states: stateArr,
          shape,
          scopeLabel,
          empty: shape.kind === 'empty',
        };
      }),
    ];

    // Population aggregator: which counties' centroids fall inside the region
    // geometry, and what's their combined population × death rate.
    const countyInRegion = (county, region) => {
      const shape = region.shape;
      if (shape.kind === 'circle') {
        return haversineMiles(shape.lat, shape.lng, county.lat, county.lng) <= shape.miles;
      }
      if (shape.kind === 'polygon') {
        return pointInPolygon(county.lat, county.lng, shape.polygon);
      }
      if (shape.kind === 'states') {
        return shape.stateSet.has(county.state);
      }
      if (shape.kind === 'counties') {
        return shape.fipsSet.has(county.fips);
      }
      return false;
    };

    return sources.map(region => {
      const lfgIn = region.empty ? [] : allLfgLocations.filter(l => homeInRegion(l, region));
      const compIn = region.empty ? [] : allCompetitorHomes.filter(c => homeInRegion(c, region));
      const lfgCalls = lfgIn.reduce((s, l) => s + (l.estimated_annual_calls || 0), 0);
      const compKnownCalls = compIn.reduce((s, c) => s + (c.estimated_annual_calls || 0), 0);
      const compWithCalls = compIn.filter(c => c.estimated_annual_calls != null && c.estimated_annual_calls > 0).length;
      const totalHomes = lfgIn.length + compIn.length;

      // Region population — kept as context (NOT used for share, since deaths
      // in a county don't equal funerals performed in that county).
      let regionPopulation = 0;
      if (!region.empty) {
        for (const county of US_COUNTY_CENTROIDS) {
          if (countyInRegion(county, region)) {
            regionPopulation += countyPopulations[county.fips] || 0;
          }
        }
      }

      const homeShare = totalHomes > 0 ? Math.round((lfgIn.length / totalHomes) * 1000) / 10 : null;
      // Call share = LFG calls / (LFG + competitor known calls). Only meaningful
      // when at least some competitors have call data; coverage indicator below
      // tells the user how complete the picture is.
      const totalKnownCalls = lfgCalls + compKnownCalls;
      const callShare = totalKnownCalls > 0 && lfgCalls > 0
        ? Math.round((lfgCalls / totalKnownCalls) * 1000) / 10
        : null;
      const competitorCoverage = compIn.length > 0
        ? Math.round((compWithCalls / compIn.length) * 100)
        : null;
      return {
        ...region,
        lfgIn, compIn,
        lfgHomes: lfgIn.length, competitorHomes: compIn.length,
        totalHomes, lfgCalls, compKnownCalls, compWithCalls,
        regionPopulation,
        homeShare, callShare, competitorCoverage,
      };
    }).filter(r => !stateFilter
      || r.states.includes(stateFilter)
      || r.lfgIn.some(l => l.state === stateFilter)
      || r.compIn.some(c => c.state === stateFilter));
  }, [allLfgLocations, allCompetitorHomes, territoriesData, countyPopulations, stateFilter]);

  const totalLfg = byLocation.length;
  const noCompetitors = allCompetitorHomes.length === 0;

  // Shared "states present" list for the Obit and Trends panels' scope
  // pickers — derived from every home we track, not just LFG's own.
  const statesPresent = useMemo(() => (
    Array.from(new Set([...allLfgLocations, ...allCompetitorHomes].map(h => h.state).filter(Boolean))).sort()
  ), [allLfgLocations, allCompetitorHomes]);

  // Scrape status summary for ObitMethodologyPanel — Phase L's cron is
  // disabled (see CLAUDE.md), so this mostly serves to make that staleness
  // visible via "last update" rather than imply it's still live.
  const scrapeProgress = useMemo(() => {
    const total = obitRows.length;
    const success = obitRows.filter(r => r.obit_count_estimation_status === 'success').length;
    const noData = obitRows.filter(r => r.obit_count_estimation_status === 'no_data').length;
    const pending = obitRows.filter(r => !r.obit_count_estimation_status).length;
    const lastScrapedAt = obitRows.reduce((max, r) => {
      const t = r.obit_count_last_estimated_at;
      return t && (!max || t > max) ? t : max;
    }, null);
    return { total, done: success + noData, success, noData, pending, lastScrapedAt };
  }, [obitRows]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}
          >
            Market Share
          </h1>
          <p className="text-sm mt-1" style={{ color: '#475569' }}>
            LFG presence vs. all other funeral homes — per location, per territory, per county.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowDiscover(true)}
              className="h-9 inline-flex items-center gap-1.5 px-3 text-[13px] font-medium rounded-lg text-white transition-all hover:shadow-md"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)' }}
            >
              <Sparkles className="w-4 h-4" /> Discover competitors
            </button>
            <button
              type="button"
              onClick={captureSnapshot}
              disabled={capturingSnapshot}
              className="h-9 inline-flex items-center gap-1.5 px-3 text-[13px] font-medium rounded-lg transition-colors hover:bg-slate-50 disabled:opacity-60"
              style={{ backgroundColor: 'white', border: '1px solid #E5E9F0', color: '#334155' }}
              title="Snapshot today's home/call counts so the trend chart has a new point"
            >
              {capturingSnapshot
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <TrendingUp className="w-4 h-4" />}
              Capture snapshot
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="h-9 inline-flex items-center gap-1.5 px-3 text-[13px] font-medium rounded-lg transition-colors hover:bg-slate-50"
              style={{ backgroundColor: 'white', border: '1px solid #E5E9F0', color: '#334155' }}
            >
              <Plus className="w-4 h-4" /> Add manually
            </button>
            {snapshotMsg && (
              <span className="text-[12px] font-medium" style={{ color: snapshotMsg.kind === 'ok' ? '#15803d' : '#B91C1C' }}>
                {snapshotMsg.text}
              </span>
            )}
          </div>
        )}
      </div>

      {noCompetitors && !loading && (
        <div
          className="mb-6 rounded-xl px-4 py-3 flex items-start gap-3"
          style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="text-[13px]">
            <div className="font-semibold">No competitor homes loaded yet.</div>
            <div className="mt-1">
              Share calculations will show LFG-only counts. Run discovery via the Discover competitors button or add competitors manually.
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl px-4 py-3 text-[13px]" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', color: '#B91C1C' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#1A3665' }} />
          <p className="text-sm" style={{ color: '#64748B' }}>Computing market share…</p>
        </div>
      ) : (
        <>

          {/* Phase M — Deaths-based market share (primary signal) */}
          <CountyDeathsPanel
            rows={countyShare}
            trend={countyShareTrend}
            territories={territoriesData || []}
            scope={trendScope}
            setScope={setTrendScope}
          />

          {/* Data explorer — every county, every signal, sortable + filterable,
              with a multi-select comparison chart. The point isn't to pick a
              "right" number for you — it's to make everything pokeable so
              whoever's doing the actual analysis can slice it themselves. */}
          <div className="mt-6">
            <CountyExplorerPanel
              rows={countyShare}
              trend={countyShareTrend}
              obitRows={obitRows}
              populations={countyPopulations}
              statesPresent={statesPresent}
            />
          </div>

          {/* Snapshot trend — LFG's share of obits over time, wherever we've
              captured a snapshot. Historical/obit-based, kept for whoever
              wants to see momentum even though Phase M is the primary number. */}
          <div className="mt-6">
            <TrendsPanel
              scope={trendScope}
              setScope={setTrendScope}
              data={trendData}
              loading={trendLoading}
              metric={trendMetric}
              setMetric={setTrendMetric}
              statesPresent={statesPresent}
              territories={territoriesData || []}
            />
          </div>

          {/* Phase L — obit-scraped, per-home cross-reference. This cron is
              actually active (pg_cron every minute, see CLAUDE.md) — the
              "last update" timestamps below reflect a genuinely live feed,
              not a frozen snapshot. */}
          <div className="mt-6">
            <ObitMethodologyPanel
              rows={obitRows}
              metric={obitMetric}
              setMetric={setObitMetric}
              scope={obitScope}
              setScope={setObitScope}
              statesPresent={statesPresent}
              territories={territoriesData || []}
              scrapeProgress={scrapeProgress}
              scrapeHealth={scrapeHealth}
            />
          </div>

          {/* Home-vs-home picker — the question a local ops manager asks
              ("how is THIS chapel doing against THAT specific rival"),
              which county-level Phase M share can't answer and the
              aggregate obit panel above doesn't make easy either. */}
          <div className="mt-6">
            <HomeComparePanel rows={obitRows} darkMode={darkMode} />
          </div>
        </>
      )}

      {showAdd && <AddCompetitorModal organization={organization} onClose={() => setShowAdd(false)} onSaved={refresh} />}
      {showDiscover && <DiscoverCompetitorsModal organization={organization} onClose={() => setShowDiscover(false)} onComplete={refresh} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CountyDeathsPanel — Phase M. Per-county market share computed as
//   sum(LFG chapel estimated_annual_calls in county) / county annual deaths
// Death counts come from U.S. Census Bureau PEP (Vintage 2025), which
// sources from NCHS and publishes annually in March. Cross-county service
// moves are treated as negligible (offsetting at portfolio scale).
// ---------------------------------------------------------------------------
function CountyDeathsPanel({ rows, trend, territories, scope, setScope }) {
  const [filter, setFilter] = React.useState('all'); // 'all' | state code | territory id

  const statesPresent = Array.from(new Set((rows || []).map(r => r.state).filter(Boolean))).sort();

  const inFilter = (r) => {
    if (filter === 'all') return true;
    if (filter.startsWith('state:')) return r.state === filter.split(':')[1];
    if (filter.startsWith('region:')) {
      const t = territories.find(x => x.id === filter.split(':')[1]);
      if (!t) return false;
      if (t.county_fips && t.county_fips.length) return t.county_fips.includes(r.county_fips);
      if (t.states && t.states.length) return t.states.includes(r.state);
      return false;
    }
    return true;
  };

  const filtered = (rows || []).filter(inFilter);
  const sortedRows = [...filtered].sort((a, b) => (b.lfg_share_pct || 0) - (a.lfg_share_pct || 0));
  const totalCalls  = filtered.reduce((s, r) => s + (Number(r.lfg_calls) || 0), 0);
  const totalDeaths = filtered.reduce((s, r) => s + (Number(r.county_deaths) || 0), 0);
  const aggregateShare = totalDeaths > 0 ? (100 * totalCalls / totalDeaths) : 0;
  const totalChapels = filtered.reduce((s, r) => s + (Number(r.chapel_count) || 0), 0);
  const totalRevenue = filtered.reduce((s, r) => s + (Number(r.projected_revenue) || 0), 0);
  const countiesWithRevenue = filtered.filter(r => (Number(r.projected_revenue) || 0) > 0).length;
  const sourceLabel = filtered[0]?.source_label || 'Census PEP';
  const deathsYear  = filtered[0]?.deaths_year || '—';

  // Trend by county — group for sparklines
  const trendByFips = (trend || []).reduce((acc, t) => {
    if (!inFilter(t)) return acc;
    (acc[t.county_fips] ||= []).push(t);
    return acc;
  }, {});

  return (
    <SectionCard
      title="Market share by county"
      count={filtered.length}
      icon={BarChart3}
      accent="#10B981"
      action={(
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="h-7 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
            style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
          >
            <option value="all">All LFG counties</option>
            {statesPresent.length > 0 && (
              <optgroup label="By state">
                {statesPresent.map(s => <option key={s} value={`state:${s}`}>{s}</option>)}
              </optgroup>
            )}
            {territories.length > 0 && (
              <optgroup label="By territory">
                {territories.map(t => <option key={t.id} value={`region:${t.id}`}>{t.name}</option>)}
              </optgroup>
            )}
          </select>
          <div className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded" style={{ backgroundColor: '#F0FDF4', color: '#166534', border: '1px solid #BBF7D0' }}>
            {sourceLabel} · {deathsYear}
          </div>
        </div>
      )}
    >
      <div className="px-5 pb-5">
        {/* Summary band */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg p-3" style={{ backgroundColor: '#EFF4FB', border: '1px solid #DBE6F5' }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#1A3665' }}>LFG calls</div>
            <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#1A3665' }}>{totalCalls.toLocaleString()}</div>
            <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>{totalChapels} chapel{totalChapels === 1 ? '' : 's'}</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#A16207' }}>County deaths</div>
            <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#A16207' }}>{totalDeaths.toLocaleString()}</div>
            <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>{deathsYear} annual</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: '#DCFCE7', border: '1px solid #BBF7D0' }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#15803D' }}>Aggregate share</div>
            <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#15803D' }}>{aggregateShare.toFixed(1)}%</div>
            <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>across {filtered.length} {filtered.length === 1 ? 'county' : 'counties'}</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: '#F5F3FF', border: '1px solid #DDD6FE' }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#6D28D9' }}>Projected revenue</div>
            <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#6D28D9' }}>{totalRevenue > 0 ? `$${(totalRevenue / 1_000_000).toFixed(1)}M` : '—'}</div>
            <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>
              {countiesWithRevenue < filtered.length
                ? `${countiesWithRevenue} of ${filtered.length} counties have ticket data`
                : `${filtered.length} ${filtered.length === 1 ? 'county' : 'counties'} priced`}
            </div>
          </div>
        </div>

        {/* Per-county table */}
        {sortedRows.length === 0 ? (
          <div className="py-8 text-center text-[13px] rounded-lg" style={{ color: '#94A3B8', backgroundColor: '#FAFBFD', border: '1px dashed #E5E9F0' }}>
            No counties to show. Filter selection has no matching LFG locations.
          </div>
        ) : (
          <div className="max-h-[480px] overflow-y-auto rounded-lg" style={{ border: '1px solid #F0F2F6' }}>
            <table className="w-full text-[12px]">
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#FAFBFD', zIndex: 1 }}>
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>County</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>State</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Chapels</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>LFG calls</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Deaths</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Share</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title="Projected annual revenue = sum(LFG calls × avg_ticket per chapel). Toledo cluster has ticket data (VYTL); other clusters pending Passare wiring.">Revenue</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title="Dominant brand tier by call volume in this county">Tier</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => {
                  const series = (trendByFips[r.county_fips] || []).sort((a, b) => a.deaths_year - b.deaths_year);
                  const shareColor = (r.lfg_share_pct || 0) >= 25 ? '#15803D' : (r.lfg_share_pct || 0) >= 10 ? '#0F172A' : '#64748B';
                  const rev = Number(r.projected_revenue) || 0;
                  const tierBadge = r.dominant_tier === 'premium'   ? { bg: '#EFF4FB', fg: '#1A3665', label: 'Premium' }
                                  : r.dominant_tier === 'mid'       ? { bg: '#FEF3C7', fg: '#A16207', label: 'Mid' }
                                  : r.dominant_tier === 'cremation' ? { bg: '#F5F3FF', fg: '#6D28D9', label: 'Cremation' }
                                  : null;
                  return (
                    <tr key={r.county_fips} style={{ borderTop: '1px solid #F0F2F6' }}
                        title={`${r.county_name}, ${r.state}\n${r.chapel_count} LFG chapel(s) · ${Number(r.lfg_calls).toLocaleString()} annual calls${r.avg_ticket ? ` · $${Number(r.avg_ticket).toLocaleString()} avg ticket` : ''}\n${Number(r.county_deaths).toLocaleString()} deaths in ${r.deaths_year} (${r.period_note || ''})\nShare = LFG calls / county deaths${rev > 0 ? `\nProjected revenue = $${rev.toLocaleString()}` : ''}`}>
                      <td className="px-3 py-1.5" style={{ color: '#0F172A' }}>{r.county_name}</td>
                      <td className="px-3 py-1.5" style={{ color: '#475569' }}>{r.state}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#475569' }}>{r.chapel_count}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#0F172A' }}>{Number(r.lfg_calls).toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#475569' }}>{Number(r.county_deaths).toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold" style={{ color: shareColor }}>
                        {r.lfg_share_pct != null ? `${Number(r.lfg_share_pct).toFixed(1)}%` : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: rev > 0 ? '#0F172A' : '#CBD5E1' }}>
                        {rev > 0 ? `$${(rev / 1_000_000).toFixed(2)}M` : '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        {tierBadge && (
                          <span className="inline-block text-[9px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: tierBadge.bg, color: tierBadge.fg }}>
                            {tierBadge.label}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <CountyShareSparkline series={series} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: '#94A3B8' }}>
          <strong style={{ color: '#475569' }}>Methodology:</strong> Share = sum of LFG chapel <code>estimated_annual_calls</code> in a county / annual deaths in that county.
          Death counts: U.S. Census Bureau Population Estimates Program ({sourceLabel}), sourced from NCHS. Cross-county service moves are treated as negligible (they roughly offset at portfolio scale).
          <br/>
          <strong style={{ color: '#475569' }}>Trend</strong> sparkline reflects death-count drift over time — LFG call volume is currently a single annual estimate per chapel. Per-year LFG history will replace the flat-numerator trend once Passare is wired up.
        </div>
      </div>
    </SectionCard>
  );
}

// Tiny inline SVG sparkline for the trend column. No chart library.
function CountyShareSparkline({ series }) {
  if (!series || series.length < 2) return <span style={{ color: '#CBD5E1' }}>—</span>;
  const w = 80, h = 22, pad = 2;
  const vals = series.map(s => s.lfg_share_pct == null ? 0 : Number(s.lfg_share_pct));
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = Math.max(0.01, max - min);
  const pts = vals.map((v, i) => {
    const x = pad + (i * (w - pad * 2)) / (vals.length - 1);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const last = vals[vals.length - 1];
  const first = vals[0];
  const trendUp = last >= first;
  return (
    <span title={series.map(s => `${s.deaths_year}: ${Number(s.lfg_share_pct).toFixed(1)}%`).join('\n')} style={{ display: 'inline-block' }}>
      <svg width={w} height={h} style={{ verticalAlign: 'middle' }}>
        <polyline points={pts} fill="none" stroke={trendUp ? '#15803D' : '#DC2626'} strokeWidth={1.5} />
      </svg>
    </span>
  );
}

// ---------------------------------------------------------------------------
// CountyExplorerPanel — sortable/filterable table across every county with
// every signal we have (LFG calls, competitor obits cross-ref, census
// deaths + vintage, population, share), plus a multi-select comparison
// chart. Not trying to pick a "right" methodology — the goal is to make
// the raw data pokeable in-app so someone who actually does market
// analysis can slice it themselves.
// ---------------------------------------------------------------------------
// Light/dark are each their own validated step set (dark isn't a filter
// over light) — see the dataviz skill's reference palette.
const EXPLORER_PALETTE_LIGHT = ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834'];
const EXPLORER_PALETTE_DARK  = ['#3987e5', '#199e70', '#c98500', '#008300', '#9085e9', '#e66767', '#d55181', '#d95926'];
const EXPLORER_PALETTE_SIZE = EXPLORER_PALETTE_LIGHT.length;
function colorForFips(fips, darkMode) {
  const palette = darkMode ? EXPLORER_PALETTE_DARK : EXPLORER_PALETTE_LIGHT;
  let hash = 0;
  for (let i = 0; i < fips.length; i++) hash = (hash * 31 + fips.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

function CountyExplorerPanel({ rows, trend, obitRows, populations, statesPresent }) {
  const { darkMode } = useTheme();
  // Search is debounced — the actual filter (searchDebounced) only updates
  // ~200ms after typing stops. Filtering on every keystroke was mounting
  // and unmounting table rows on every character, and each newly-mounted
  // row briefly showed light-mode colors before the dark-mode DOM walker
  // (see darkModeOverrides.js) caught up a frame later — a visible flicker
  // that got worse the faster someone typed.
  const [search, setSearch] = React.useState('');
  const [searchDebounced, setSearchDebounced] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 200);
    return () => clearTimeout(t);
  }, [search]);
  const [stateFilter, setStateFilter] = React.useState('all');
  const [sortKey, setSortKey] = React.useState('lfg_share_pct');
  const [sortDir, setSortDir] = React.useState('desc');
  const [selected, setSelected] = React.useState(new Set());

  // Obit cross-reference — sum last-12mo obits (all homes, LFG + competitor)
  // per county, plus an LFG-only sum so we can compute an obit-based share
  // % as an INDEPENDENT cross-check next to the Census-based share —
  // deliberately not blended into one number (see obitShare below). Also
  // tracks how many homes actually have obit data, so thin coverage (one
  // or two homes deciding the whole county's obit share) can be flagged
  // instead of presented as if it were as solid as the LFG-calls number.
  const obitByFips = React.useMemo(() => {
    const acc = {};
    for (const r of (obitRows || [])) {
      if (!r.county_fips) continue;
      const bucket = (acc[r.county_fips] ||= { total: 0, lfgTotal: 0, homesWithData: 0, lastAt: null });
      const val = Number(r.obits_last_12mo) || 0;
      bucket.total += val;
      if (r.is_lfg) bucket.lfgTotal += val;
      if (r.obits_last_12mo != null) bucket.homesWithData += 1;
      if (r.obit_count_last_estimated_at && (!bucket.lastAt || r.obit_count_last_estimated_at > bucket.lastAt)) {
        bucket.lastAt = r.obit_count_last_estimated_at;
      }
    }
    return acc;
  }, [obitRows]);

  const enriched = React.useMemo(() => (rows || []).map(r => {
    const obit = obitByFips[r.county_fips] || null;
    // Independent obit-based share — computed entirely separately from
    // the Census-based `lfg_share_pct` on the row, deliberately not
    // reconciled with it. A big gap between the two IS the signal (obit
    // posting completeness differs by home, scraping coverage differs by
    // county) — collapsing them into one number would hide exactly the
    // thing worth noticing.
    const obitSharePct = obit && obit.total > 0 ? (100 * obit.lfgTotal / obit.total) : null;
    // <3 homes with obit data deciding a county's share is a coin flip,
    // not a signal — flag it rather than presenting it at face value.
    const obitThin = obit ? obit.homesWithData < 3 : true;
    return {
      ...r,
      population: populations?.[r.county_fips] ?? null,
      obit,
      obitSharePct,
      obitThin,
    };
  }), [rows, populations, obitByFips]);

  const filtered = React.useMemo(() => {
    const q = searchDebounced.trim().toLowerCase();
    return enriched.filter(r => {
      if (stateFilter !== 'all' && r.state !== stateFilter) return false;
      if (q && !`${r.county_name} ${r.state}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [enriched, searchDebounced, stateFilter]);

  const sorted = React.useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'obit_total') { av = a.obit?.total ?? null; bv = b.obit?.total ?? null; }
      if (typeof av === 'string' || typeof bv === 'string') {
        return dir * String(av || '').localeCompare(String(bv || ''));
      }
      av = av == null ? -Infinity : Number(av);
      bv = bv == null ? -Infinity : Number(bv);
      return dir * (av - bv);
    });
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleSelect = (fips) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(fips)) next.delete(fips);
      else if (next.size < EXPLORER_PALETTE_SIZE) next.add(fips);
      return next;
    });
  };

  const compareSeries = React.useMemo(() => {
    const byFips = {};
    for (const t of (trend || [])) {
      if (!selected.has(t.county_fips)) continue;
      (byFips[t.county_fips] ||= []).push(t);
    }
    return Array.from(selected).map(fips => {
      const points = (byFips[fips] || [])
        .sort((a, b) => a.deaths_year - b.deaths_year)
        .map(t => ({ year: t.deaths_year, value: Number(t.lfg_share_pct) || 0 }));
      const meta = enriched.find(r => r.county_fips === fips);
      return { fips, label: meta ? `${meta.county_name}, ${meta.state}` : fips, color: colorForFips(fips, darkMode), points };
    });
  }, [selected, trend, enriched]);

  const SortHeader = ({ label, k, align = 'right' }) => (
    <th
      className={`px-3 py-2 text-[10px] uppercase tracking-wider cursor-pointer select-none whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}`}
      style={{ color: sortKey === k ? '#1A3665' : '#94A3B8' }}
      onClick={() => toggleSort(k)}
    >
      {label}{sortKey === k && <span style={{ marginLeft: 3 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <SectionCard
      title="Explore & compare counties"
      count={sorted.length}
      icon={GitCompare}
      accent="#7C3AED"
      action={(
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2" style={{ color: '#94A3B8' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search county…"
              className="h-7 text-[12px] rounded-md pl-7 pr-2 focus:outline-none"
              style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A', width: 160 }}
            />
          </div>
          <select
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            className="h-7 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
            style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
          >
            <option value="all">All states</option>
            {statesPresent.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
    >
      <div className="px-5 pb-5">
        <div className="text-[11px] mb-3" style={{ color: '#94A3B8' }}>
          Click a column header to sort. Check up to {EXPLORER_PALETTE_SIZE} counties to compare their share trend below.
        </div>
        <div className="max-h-[420px] overflow-y-auto rounded-lg" style={{ border: '1px solid #F0F2F6' }}>
          <table className="w-full text-[12px]">
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#FAFBFD', zIndex: 1 }}>
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <SortHeader label="County" k="county_name" align="left" />
                <SortHeader label="State" k="state" align="left" />
                <SortHeader label="Chapels" k="chapel_count" />
                <SortHeader label="LFG calls" k="lfg_calls" />
                <SortHeader label="Deaths" k="county_deaths" />
                <SortHeader label="Population" k="population" />
                <SortHeader label="Share" k="lfg_share_pct" />
                <SortHeader label="Obit Share" k="obitSharePct" />
                <SortHeader label="Obits (12mo)" k="obit_total" />
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                const isSelected = selected.has(r.county_fips);
                const obitAge = r.obit?.lastAt
                  ? new Date(r.obit.lastAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : null;
                return (
                  <tr key={r.county_fips} style={{ borderTop: '1px solid #F0F2F6', backgroundColor: isSelected ? (darkMode ? 'rgba(124, 58, 237, 0.18)' : '#F5F3FF') : undefined }}>
                    <td className="px-3 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(r.county_fips)}
                        disabled={!isSelected && selected.size >= EXPLORER_PALETTE_SIZE}
                      />
                    </td>
                    <td className="px-3 py-1.5" style={{ color: '#0F172A' }}>
                      {isSelected && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, backgroundColor: colorForFips(r.county_fips, darkMode), marginRight: 6 }} />}
                      {r.county_name}
                    </td>
                    <td className="px-3 py-1.5" style={{ color: '#475569' }}>{r.state}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#475569' }}>{r.chapel_count}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#0F172A' }}>{Number(r.lfg_calls).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#475569' }} title={`${r.source_label || 'Census PEP'} · ${r.deaths_year}`}>{Number(r.county_deaths).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#475569' }}>{r.population != null ? Number(r.population).toLocaleString() : '—'}</td>
                    <td className="px-3 py-1.5 text-right"><SharePill pct={r.lfg_share_pct != null ? Number(r.lfg_share_pct) : null} /></td>
                    <td className="px-3 py-1.5 text-right"
                        title={r.obitSharePct != null
                          ? `${r.obit.homesWithData} home(s) with obit data in this county${r.obitThin ? ' — thin coverage, treat as directional only' : ''}`
                          : 'No obit data for this county'}>
                      {r.obitSharePct != null
                        ? <span className="inline-flex items-center gap-1 justify-end">
                            <SharePill pct={r.obitSharePct} />
                            {r.obitThin && <AlertTriangle className="w-3 h-3" style={{ color: '#D97706' }} />}
                          </span>
                        : <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#94A3B8' }} title={obitAge ? `Last scraped ${obitAge}` : 'No obit data for this county'}>
                      {r.obit?.total ? r.obit.total.toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selected.size > 0 && (
          <div className="mt-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#64748B' }}>
              Share % trend — {selected.size} selected
            </div>
            <MultiCountyChart series={compareSeries} darkMode={darkMode} />
          </div>
        )}

        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: '#94A3B8' }}>
          <strong style={{ color: '#475569' }}>Deaths</strong> and <strong style={{ color: '#475569' }}>Share</strong> are the Phase M (canonical) signal — Census PEP-sourced, updated annually, comprehensive (includes deaths with no published obituary).
          <br/>
          <strong style={{ color: '#475569' }}>Obit Share</strong> is computed entirely separately — LFG's share of obits among tracked homes in that county — and deliberately not reconciled with the Census-based Share column. A large gap between the two is itself worth noticing (obit-posting completeness varies by home, scraping coverage varies by county) rather than something to average away. ⚠ flags counties where fewer than 3 homes have obit data — treat those as directional, not a real percentage.
          <br/>
          <strong style={{ color: '#475569' }}>Obits (12mo)</strong> is the raw total behind that share. Hover it for the last-scraped date.
        </div>
      </div>
    </SectionCard>
  );
}

// Multi-series line chart for the county comparison view. Same hand-rolled
// SVG approach as Sparkline/CountyShareSparkline (no chart-library
// dependency) — one path per selected county, colored by a stable
// hash-of-fips so a county's color never shifts as the selection changes.
//
// Colors are resolved directly from `darkMode` rather than left as
// light-mode hex for the global DOM-walker to patch after the fact: this
// chart mounts fresh the moment someone checks their first county, and
// raw SVG fill/stroke attributes (as opposed to the `style` prop) aren't
// something that walker touches at all — so without this it would have
// stayed light-on-dark permanently, not just flickered once.
function MultiCountyChart({ series, darkMode }) {
  const W = 760, H = 220, padX = 44, padY = 20;
  const surface  = darkMode ? '#1c2230' : '#FAFBFD';
  const border   = darkMode ? '#1e2430' : '#F0F2F6';
  const gridline = darkMode ? '#252b38' : '#E5E9F0';
  const axisText = darkMode ? '#94a3b8' : '#94A3B8';
  const allYears = Array.from(new Set(series.flatMap(s => s.points.map(p => p.year)))).sort((a, b) => a - b);
  if (allYears.length === 0) {
    return (
      <div className="py-8 text-center text-[13px] rounded-lg" style={{ color: axisText, backgroundColor: surface, border: `1px dashed ${border}` }}>
        No trend history yet for the selected counties — Phase M has only one data point per county so far in most cases.
      </div>
    );
  }
  const allValues = series.flatMap(s => s.points.map(p => p.value));
  const minV = Math.min(0, ...allValues);
  const maxV = Math.max(10, ...allValues) * 1.1;
  const range = Math.max(maxV - minV, 1);
  const yearSpan = Math.max(allYears[allYears.length - 1] - allYears[0], 1);
  const xFor = (year) => allYears.length === 1 ? W / 2 : padX + ((year - allYears[0]) / yearSpan) * (W - 2 * padX);
  const yFor = (v) => padY + (1 - (v - minV) / range) * (H - 2 * padY);

  return (
    <div>
      <div className="w-full overflow-hidden rounded-lg" style={{ backgroundColor: surface, border: `1px solid ${border}` }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: 240 }}>
          {[0.25, 0.5, 0.75].map(f => {
            const v = minV + f * range;
            const y = yFor(v);
            return (
              <g key={f}>
                <line x1={padX} y1={y} x2={W - padX} y2={y} stroke={gridline} strokeDasharray="2 3" />
                <text x={padX - 6} y={y + 3} textAnchor="end" fontSize="9" fill={axisText}>{v.toFixed(0)}%</text>
              </g>
            );
          })}
          {allYears.map(y => (
            <text key={y} x={xFor(y)} y={H - 4} textAnchor="middle" fontSize="9" fill={axisText}>{y}</text>
          ))}
          {series.map(s => {
            if (s.points.length === 0) return null;
            const path = s.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(p.year).toFixed(1)},${yFor(p.value).toFixed(1)}`).join(' ');
            return (
              <g key={s.fips}>
                <path d={path} fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                {s.points.map((p, i) => (
                  <circle key={i} cx={xFor(p.year)} cy={yFor(p.value)} r="3.5" fill={s.color} stroke={surface} strokeWidth="1.5">
                    <title>{s.label} · {p.year}: {p.value.toFixed(1)}%</title>
                  </circle>
                ))}
              </g>
            );
          })}
        </svg>
      </div>
      {/* Legend — always present for 2+ series; also serves as the required
          table-view fallback for palette slots that don't clear 3:1 contrast. */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2">
        {series.map(s => {
          const last = s.points[s.points.length - 1];
          return (
            <div key={s.fips} className="flex items-center gap-1.5 text-[11px]" style={{ color: '#475569' }}>
              <span style={{ display: 'inline-block', width: 10, height: 2, backgroundColor: s.color }} />
              <span>{s.label}</span>
              {last && <span className="tabular-nums font-semibold" style={{ color: '#0F172A' }}>{last.value.toFixed(1)}%</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HomeComparePanel — pick specific LFG chapel(s) and competitor home(s) and
// compare their obit counts directly. Complementary to Phase M (county-level,
// portfolio-facing) — this answers "how is THIS chapel doing against THAT
// specific rival," which a county aggregate can't. Current-snapshot only:
// no per-home historical trend exists yet (market_share_snapshots captures
// scope-level aggregates, not individual homes), so this is a bar
// comparison of right-now counts, not a trend line — noted in the UI
// rather than faked with a single-point "trend."
// ---------------------------------------------------------------------------
function HomeComparePanel({ rows, darkMode }) {
  const [search, setSearch] = React.useState('');
  const [searchDebounced, setSearchDebounced] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 200);
    return () => clearTimeout(t);
  }, [search]);
  const [stateFilter, setStateFilter] = React.useState('all');
  const [metric, setMetric] = React.useState('last_12mo'); // 'last_12mo' | 'ytd'
  const [selected, setSelected] = React.useState(new Set());

  const statesPresent = React.useMemo(() => (
    Array.from(new Set((rows || []).map(r => r.state).filter(Boolean))).sort()
  ), [rows]);

  // Confidence read-out from the notes text the scraper already writes —
  // cheaper than a new column, and keeps this in sync with whatever the
  // scraper's notes format says without a second source of truth.
  // siblingCount distinguishes the two real causes of a >=1000 sitemap
  // reading: a genuine multi-location chain feed (sibling_count > 1, real
  // combined volume) vs. a solo home whose CMS bulk-touched every historical
  // obit's lastmod at once (site migration/republish) — at real-world call
  // volumes (~150-300/yr even for busy homes) a solo home can't organically
  // produce 1000 obits in a 12mo window, so for sibling_count === 1 this is
  // almost always the bulk-touch artifact, not a true volume cap.
  const confidenceFor = (notes, siblingCount, dataUnreliable, last12moEstimated) => {
    if (dataUnreliable) return { label: 'Data unreliable — both windows capped', tone: 'bad' };
    if (!notes) return { label: 'No data yet', tone: 'muted' };
    if (notes.includes('ai-listing')) return { label: 'AI-read listing page (est.)', tone: 'warn' };
    if (notes.includes('No obit sitemap found')) return { label: 'No data found', tone: 'muted' };
    const sampleMatch = notes.match(/Sample:\s*([a-z-]+)/i);
    const via = sampleMatch ? sampleMatch[1] : null;
    let label = 'Matched';
    if (via === 'addr') label = 'Matched via address';
    else if (via && via.startsWith('chapel-name')) label = 'Matched via name';
    else if (via === 'housenum+city') label = 'Matched via address+city';
    const aiSite = notes.includes('[AI-discovered site]');
    const capHit = notes.includes('platform cap hit');
    const isChain = (siblingCount || 1) > 1;
    const capNote = last12moEstimated ? ' · annualized from YTD' : capHit ? (isChain ? ' · chain, capped' : ' · dates unreliable') : '';
    return {
      label: label + (aiSite ? ' · AI-guessed site' : '') + capNote,
      tone: aiSite || (capHit && !last12moEstimated) ? 'warn' : 'ok',
    };
  };

  const filtered = React.useMemo(() => {
    const q = searchDebounced.trim().toLowerCase();
    return (rows || []).filter(r => {
      if (stateFilter !== 'all' && r.state !== stateFilter) return false;
      if (q && !`${r.name} ${r.city} ${r.state}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, searchDebounced, stateFilter]);

  const sorted = React.useMemo(() => (
    [...filtered].sort((a, b) => (Number(b[metric]) || 0) - (Number(a[metric]) || 0))
  ), [filtered, metric]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < EXPLORER_PALETTE_SIZE) next.add(id);
      return next;
    });
  };

  const compareRows = React.useMemo(() => (
    (rows || []).filter(r => selected.has(r.id))
  ), [rows, selected]);

  return (
    <SectionCard
      title="Compare specific homes"
      count={sorted.length}
      icon={GitCompare}
      accent="#0891B2"
      action={(
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2" style={{ color: '#94A3B8' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search home…"
              className="h-7 text-[12px] rounded-md pl-7 pr-2 focus:outline-none"
              style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A', width: 160 }}
            />
          </div>
          <select
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            className="h-7 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
            style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
          >
            <option value="all">All states</option>
            {statesPresent.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="inline-flex rounded-md overflow-hidden" style={{ border: '1px solid #E5E9F0' }}>
            {[{ id: 'last_12mo', label: 'Last 12mo' }, { id: 'ytd', label: 'YTD' }].map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMetric(m.id)}
                className="h-7 px-2.5 text-[11px] font-semibold uppercase tracking-wider"
                style={metric === m.id
                  ? { backgroundColor: '#1A3665', color: 'white' }
                  : { backgroundColor: 'white', color: '#475569' }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      )}
    >
      <div className="px-5 pb-5">
        <div className="text-[11px] mb-3" style={{ color: '#94A3B8' }}>
          Click a home to add it to the comparison below — up to {EXPLORER_PALETTE_SIZE} at once. Mix LFG chapels and competitors freely.
        </div>
        <div className="max-h-[360px] overflow-y-auto rounded-lg" style={{ border: '1px solid #F0F2F6' }}>
          <table className="w-full text-[12px]">
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#FAFBFD', zIndex: 1 }}>
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Home</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>City/State</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>{metric === 'ytd' ? 'YTD' : 'Last 12mo'}</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                const isSelected = selected.has(r.id);
                const conf = confidenceFor(r.notes, r.sibling_count, r.data_unreliable, r.last_12mo_estimated);
                const confColor = conf.tone === 'ok' ? '#15803D' : conf.tone === 'warn' ? '#A16207' : conf.tone === 'bad' ? '#DC2626' : '#94A3B8';
                return (
                  <tr
                    key={r.id}
                    onClick={() => toggleSelect(r.id)}
                    className="cursor-pointer"
                    style={{ borderTop: '1px solid #F0F2F6', backgroundColor: isSelected ? 'rgba(8,145,178,0.08)' : undefined }}
                  >
                    <td className="px-3 py-1.5 text-center">
                      <input type="checkbox" checked={isSelected} readOnly disabled={!isSelected && selected.size >= EXPLORER_PALETTE_SIZE} />
                    </td>
                    <td className="px-3 py-1.5" style={{ color: '#0F172A' }}>
                      {isSelected && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, backgroundColor: colorForFips(r.id, darkMode), marginRight: 6 }} />}
                      {r.is_lfg && <span className="inline-block text-[9px] font-bold uppercase px-1 rounded mr-1" style={{ backgroundColor: '#EFF4FB', color: '#1A3665' }}>LFG</span>}
                      {r.is_consolidator && !r.is_lfg && <span className="inline-block text-[9px] font-bold uppercase px-1 rounded mr-1" style={{ backgroundColor: '#FEF3C7', color: '#A16207' }}>Chain</span>}
                      {r.name}
                    </td>
                    <td className="px-3 py-1.5" style={{ color: '#475569' }}>{r.city}, {r.state}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold" style={{ color: '#0F172A' }}>
                      {r[metric] != null ? Number(r[metric]).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-[11px]" style={{ color: confColor }}>{conf.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {compareRows.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#64748B' }}>
              {compareRows.length} selected — {metric === 'ytd' ? 'YTD' : 'last 12 months'} obit count
            </div>
            <HomeCompareBarChart rows={compareRows} metric={metric} darkMode={darkMode} />
          </div>
        )}

        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: '#94A3B8' }}>
          Current snapshot only — there's no per-home history captured yet, so this shows where things stand right now, not a trend over time. "Confidence" reflects how the scraper matched each obituary to this specific home; treat AI-guessed-site and capped results as directional, not exact.
        </div>
      </div>
    </SectionCard>
  );
}

// Horizontal bar comparison — hand-rolled SVG, same convention as the
// other charts on this page. Theme-aware for the same reason as
// MultiCountyChart: this mounts fresh the moment a second home is picked.
function HomeCompareBarChart({ rows, metric, darkMode }) {
  const axisText = darkMode ? '#94a3b8' : '#94A3B8';
  const gridline = darkMode ? '#252b38' : '#E5E9F0';
  const trackBg = darkMode ? '#1c2230' : '#FAFBFD';
  const sorted = [...rows].sort((a, b) => (Number(b[metric]) || 0) - (Number(a[metric]) || 0));
  const maxVal = Math.max(1, ...sorted.map(r => Number(r[metric]) || 0));
  const barH = 26, gap = 10, padL = 4, labelW = 190;
  const H = sorted.length * (barH + gap);
  const W = 640;
  const trackW = W - labelW - 50;

  return (
    <div className="w-full rounded-lg p-3" style={{ backgroundColor: trackBg, border: `1px solid ${gridline}` }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: Math.max(80, H) }}>
        {sorted.map((r, i) => {
          const val = Number(r[metric]) || 0;
          const barW = Math.max(2, (val / maxVal) * trackW);
          const y = i * (barH + gap);
          const color = colorForFips(r.id, darkMode);
          return (
            <g key={r.id}>
              <text x={padL} y={y + barH / 2 + 4} fontSize="11" fill={axisText}>
                {r.name.length > 26 ? r.name.slice(0, 25) + '…' : r.name}
              </text>
              <rect x={labelW} y={y} width={trackW} height={barH} rx={4} fill={darkMode ? '#252b38' : '#F0F2F6'} />
              <rect x={labelW} y={y} width={barW} height={barH} rx={4} fill={color}>
                <title>{r.name}: {val.toLocaleString()}</title>
              </rect>
              <text x={labelW + barW + 8} y={y + barH / 2 + 4} fontSize="11" fontWeight="600" fill={darkMode ? '#e2e8f0' : '#0F172A'}>
                {val.toLocaleString()}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ObitMethodologyPanel — Phase L, kept live as of Phase L.19 (Jul 2026) as a
// secondary/cross-reference signal. CountyDeathsPanel (Phase M) remains the
// primary market-share signal.
// ---------------------------------------------------------------------------
function ObitMethodologyPanel({
  rows, metric, setMetric,
  scope, setScope, statesPresent, territories,
  scrapeProgress, scrapeHealth,
}) {
  // Local-market radius (miles) — share is computed against homes within this
  // radius of each FH, not the global scope total. "Noll's share in Noll's market."
  const [localRadius, setLocalRadius] = React.useState(15);

  // Filter rows by selected scope
  const inScope = (r) => {
    if (!scope || scope.type === 'overall') return true;
    if (scope.type === 'state')  return (r.state || '').toUpperCase() === (scope.value || '').toUpperCase();
    if (scope.type === 'region') {
      const t = territories.find(x => x.id === scope.value);
      if (!t) return false;
      // Polygon test if drawn; else county_fips set; else states list
      if (t.drawn_geometry && r.latitude && r.longitude) {
        try { return pointInPolygon(r.latitude, r.longitude, t.drawn_geometry); } catch { return false; }
      }
      if (t.county_fips && t.county_fips.length > 0 && r.county_fips) {
        return t.county_fips.includes(r.county_fips);
      }
      if (t.states && t.states.length > 0 && r.state) {
        return t.states.includes(r.state);
      }
      return false;
    }
    return true;
  };

  const scopedRows = (rows || []).filter(inScope);
  const dataRows = scopedRows.filter(r => r.obits_ytd != null || r.obits_last_12mo != null);
  const pendingInScope = scopedRows.length - dataRows.length;
  const lfgRows = dataRows.filter(r => r.is_lfg);
  const compRows = dataRows.filter(r => !r.is_lfg);
  const valOf = (r) => metric === 'ytd' ? (r.obits_ytd || 0) : (r.obits_last_12mo || 0);
  const lfgSum = lfgRows.reduce((s, r) => s + valOf(r), 0);
  const compSum = compRows.reduce((s, r) => s + valOf(r), 0);
  const totalSum = lfgSum + compSum;
  const lfgShare = totalSum > 0 ? (100 * lfgSum / totalSum) : 0;
  const sortedAll = [...dataRows].sort((a, b) => valOf(b) - valOf(a));

  // Local market: for each home with coords, find every other home in the
  // *full* rows list (not just scope-filtered) within `localRadius` miles.
  // Why full list, not scoped: a home near a state border competes with homes
  // across that border regardless of which state filter is active.
  // Postgres `numeric` arrives as a string via PostgREST — coerce to Number.
  // We track BOTH homes-with-data and total homes (incl. no_data / unscraped)
  // in the radius, so the UI can flag thin coverage instead of pretending
  // 100% share is real when there's only 1 home with data in the area.
  const allInRadius = (rows || [])
    .map(r => ({
      ...r,
      _lat: r.latitude  != null ? Number(r.latitude)  : null,
      _lng: r.longitude != null ? Number(r.longitude) : null,
    }))
    .filter(r => Number.isFinite(r._lat) && Number.isFinite(r._lng));
  const localMarketFor = (home) => {
    const lat = home.latitude  != null ? Number(home.latitude)  : NaN;
    const lng = home.longitude != null ? Number(home.longitude) : NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const inside = allInRadius.filter(p =>
      haversineMiles(lat, lng, p._lat, p._lng) <= localRadius
    );
    const withData = inside.filter(p => p.obits_ytd != null || p.obits_last_12mo != null);
    const total  = withData.reduce((s, p) => s + valOf(p), 0);
    const lfg    = withData.filter(p => p.is_lfg).reduce((s, p) => s + valOf(p), 0);
    return {
      peers: withData,
      total,
      lfg,
      countWithData: withData.length,
      countTotal: inside.length,            // includes no_data + unscraped
      countMissing: inside.length - withData.length,
    };
  };
  const sitemapCap = (r) => {
    const v = metric === 'ytd' ? r.sitemap_obits_ytd : r.sitemap_obits_last_12mo;
    const hit = v != null && v >= 1000;
    // At real-world volumes (~150-300 calls/yr even for a busy home) a solo
    // home can't organically produce 1000 obits in a 12mo window. When this
    // fires for sibling_count === 1 it's almost always a CMS bulk-touching
    // every historical obit's lastmod (migration/republish), not real
    // volume exceeding the platform's 1000-URL sitemap cap. It's only a
    // genuine "count may be a floor" situation for a shared chain sitemap
    // (sibling_count > 1), where combined multi-location volume can
    // plausibly cross 1000/yr.
    return { hit, isChain: hit && (r.sibling_count || 1) > 1 };
  };

  return (
    <SectionCard
      title="Market share"
      count={dataRows.length}
      icon={BarChart3}
      accent="#10B981"
      action={(
        <div className="flex items-center gap-2 flex-wrap">
          {/* Scope picker — primary control */}
          <select
            value={`${scope.type}:${scope.value || ''}`}
            onChange={e => {
              const [type, value] = e.target.value.split(/:(.+)/);
              if (type === 'overall') setScope({ type: 'overall', value: null, label: 'Overall' });
              else if (type === 'state') setScope({ type: 'state', value, label: value });
              else if (type === 'region') {
                const t = territories.find(x => x.id === value);
                setScope({ type: 'region', value, label: t?.name || 'Region' });
              }
            }}
            className="h-7 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
            style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
          >
            <option value="overall:">Overall (all homes)</option>
            {statesPresent.length > 0 && (
              <optgroup label="By state">
                {statesPresent.map(s => <option key={s} value={`state:${s}`}>{s}</option>)}
              </optgroup>
            )}
            {territories.length > 0 && (
              <optgroup label="By territory">
                {territories.map(t => <option key={t.id} value={`region:${t.id}`}>{t.name}</option>)}
              </optgroup>
            )}
          </select>
          <div className="inline-flex rounded-md overflow-hidden" style={{ border: '1px solid #E5E9F0' }}>
            {[
              { id: 'ytd',       label: 'YTD' },
              { id: 'last_12mo', label: 'Last 12mo' },
            ].map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMetric(m.id)}
                className="h-7 px-2.5 text-[11px] font-semibold uppercase tracking-wider"
                style={metric === m.id
                  ? { backgroundColor: '#1A3665', color: 'white' }
                  : { backgroundColor: 'white', color: '#475569' }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <select
            value={localRadius}
            onChange={e => setLocalRadius(Number(e.target.value))}
            className="h-7 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
            style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
            title="Local-market radius — Share column is computed against homes within this distance of each FH."
          >
            {[5, 10, 15, 25, 50].map(r => (
              <option key={r} value={r}>Local: {r} mi</option>
            ))}
          </select>
        </div>
      )}
    >
      <div className="px-5 pb-5">
        {/* Scrape status — always shown so user knows where the data came from */}
        {scrapeProgress && scrapeProgress.total > 0 && (() => {
          const isPending = scrapeProgress.pending > 0;
          const lastTxt = scrapeProgress.lastScrapedAt
            ? new Date(scrapeProgress.lastScrapedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
            : null;
          return (
            <div className="mb-3 rounded-lg px-3 py-2 text-[12px] flex items-center justify-between"
                 style={isPending
                   ? { backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD', color: '#075985' }
                   : { backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }}>
              <span>
                {isPending
                  ? <Loader2 className="w-3 h-3 inline animate-spin mr-1.5 align-text-bottom" />
                  : <span style={{ color: '#16A34A', marginRight: 6 }}>●</span>}
                {isPending
                  ? <>Scraping in progress: <strong>{scrapeProgress.done}</strong> of {scrapeProgress.total} homes complete · <strong>{scrapeProgress.pending}</strong> pending{pendingInScope > 0 && <span style={{ color: '#0EA5E9' }}> · {pendingInScope} in this scope</span>}</>
                  : <>All <strong>{scrapeProgress.total}</strong> homes scraped · <strong>{scrapeProgress.success}</strong> with obit data · <strong>{scrapeProgress.noData}</strong> with no findable obits</>}
              </span>
              <span className="text-[11px]" style={{ color: isPending ? '#0284C7' : '#15803D' }}>
                {isPending ? 'cron runs every minute' : (lastTxt ? `last update ${lastTxt}` : 'cron runs every minute')}
              </span>
            </div>
          );
        })()}

        {/* Scrape health — reads get_obit_scrape_health, the same source
            the weekly digest email uses. "Stuck" = no successful scrape in
            21+ days despite retries, usually a site that changed structure
            or started blocking the scraper. Only shown when there's
            something to flag, to avoid duplicating the always-green
            scrapeProgress banner above on the common case. */}
        {scrapeHealth && scrapeHealth.stuck > 0 && (
          <div className="mb-3 rounded-lg px-3 py-2 text-[12px] flex items-center gap-2"
               style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              <strong>{scrapeHealth.stuck}</strong> home{scrapeHealth.stuck === 1 ? '' : 's'} stuck — no successful scrape in 21+ days despite retries. Check the Notes column below for why.
              {scrapeHealth.ai_discovered_site > 0 && <span> · {scrapeHealth.ai_discovered_site} matched via AI-guessed website (lower confidence)</span>}
              {scrapeHealth.platform_cap_hit > 0 && <span> · {scrapeHealth.platform_cap_hit} hit the 1000-entry sitemap cap in-window (real for a shared chain feed, usually a stale-timestamp artifact for a solo home — see row tooltip)</span>}
            </span>
          </div>
        )}

        {/* Summary band — scope-specific */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="rounded-lg p-3" style={{ backgroundColor: '#EFF4FB', border: '1px solid #DBE6F5' }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#1A3665' }}>LFG ({metric === 'ytd' ? 'YTD' : 'last 12mo'})</div>
            <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#1A3665' }}>{lfgSum.toLocaleString()}</div>
            <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>{lfgRows.length} {lfgRows.length === 1 ? 'chapel' : 'chapels'}</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#A16207' }}>Competitors</div>
            <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#A16207' }}>{compSum.toLocaleString()}</div>
            <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>{compRows.length} {compRows.length === 1 ? 'home' : 'homes'}</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: '#DCFCE7', border: '1px solid #BBF7D0' }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#15803D' }}>LFG share</div>
            <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#15803D' }}>{lfgShare.toFixed(1)}%</div>
            <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>
              of {scope.type === 'overall' ? 'all' : scope.label} obits combined
            </div>
          </div>
        </div>

        {/* Per-home table — simplified columns */}
        {dataRows.length === 0 ? (
          <div className="py-8 text-center text-[13px] rounded-lg" style={{ color: '#94A3B8', backgroundColor: '#FAFBFD', border: '1px dashed #E5E9F0' }}>
            No scraped homes in <span className="font-semibold" style={{ color: '#475569' }}>{scope.label}</span> yet.
            {pendingInScope > 0 && <> {pendingInScope} pending — cron will populate these as it runs.</>}
          </div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto rounded-lg" style={{ border: '1px solid #F0F2F6' }}>
            <table className="w-full text-[12px]">
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#FAFBFD', zIndex: 1 }}>
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Home</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>City</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>{metric === 'ytd' ? 'Obits YTD' : 'Obits 12mo'}</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title={`Each home's share of obits within ${localRadius} miles of itself.`}>Local share ({localRadius}mi)</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title="Homes with obit data / total homes in our DB within the local-market radius. Low ratios = sparse coverage — share is unreliable until more competitors are added.">Coverage</th>
                </tr>
              </thead>
              <tbody>
                {sortedAll.map(r => {
                  const v = valOf(r);
                  const local = localMarketFor(r);
                  const rawShare = local && local.total > 0 ? (100 * v / local.total) : null;
                  const peersWithData = local ? local.countWithData - 1 : 0;     // exclude self
                  const peersTotal    = local ? local.countTotal    - 1 : 0;
                  // Thin market = fewer than 2 OTHER homes-with-data in radius.
                  // 100% in a market of 1 is meaningless; 90% in a market of 2
                  // depends on whether the missing competitors got scraped.
                  const isThin = peersWithData < 2;
                  const truncated = sitemapCap(r);
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid #F0F2F6' }}
                        title={`Attributed (matched to this chapel): ${metric === 'ytd' ? r.obits_ytd_attributed : r.obits_last_12mo_attributed}\nSitemap total (all chain obits in window): ${metric === 'ytd' ? r.sitemap_obits_ytd : r.sitemap_obits_last_12mo}\nSiblings on shared website: ${r.sibling_count}\nReference annual: ${r.reference_annual || '—'}\n\nLocal market (${localRadius}mi):\n  ${local ? `${local.countWithData} of ${local.countTotal} homes have obit data` : 'no coords'}\n  ${local ? `${local.total.toLocaleString()} obits combined` : ''}${local && local.countMissing > 0 ? `\n  ${local.countMissing} home(s) in radius missing data — share is a ceiling, not a fact` : ''}`}>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1.5">
                          {r.is_lfg && (
                            <span className="inline-block text-[9px] font-bold uppercase px-1 rounded flex-shrink-0" style={{ backgroundColor: '#EFF4FB', color: '#1A3665' }}>LFG</span>
                          )}
                          {r.is_consolidator && !r.is_lfg && (
                            <span className="inline-block text-[9px] font-bold uppercase px-1 rounded flex-shrink-0" style={{ backgroundColor: '#FEF3C7', color: '#A16207' }}>Chain</span>
                          )}
                          <span style={{ color: '#0F172A' }} className="truncate">{r.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-1.5" style={{ color: '#475569' }}>{r.city || '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold" style={{ color: r.data_unreliable ? '#DC2626' : '#0F172A' }}>
                        {r.data_unreliable ? (
                          <span title="Both YTD and 12mo sitemap readings hit the 1000-URL cap — the entire archive appears bulk-touched with a recent timestamp, so nothing here can be trusted. Excluded from totals.">
                            n/a
                          </span>
                        ) : v > 0 ? v.toLocaleString() : '—'}
                        {r.data_unreliable ? null : metric === 'last_12mo' && r.last_12mo_estimated ? (
                          <span
                            title="Raw sitemap reading hit the 1000-URL cap (a CMS bulk-touched-timestamp artifact, not real volume) — showing YTD annualized instead"
                            style={{ color: '#0891B2', marginLeft: 4, fontSize: 10, fontWeight: 700 }}
                          >≈</span>
                        ) : truncated.hit && (
                          <span
                            title={truncated.isChain
                              ? 'Shared chain sitemap at platform cap (1000) — combined multi-location volume, actual count may be higher'
                              : 'Sitemap shows 1000+ obits modified in this window — for a single home this is almost certainly a CMS bulk-touched-timestamp artifact (site migration/republish), not real annual volume'}
                            style={{ color: '#A16207', marginLeft: 4 }}
                          >⚠</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold"
                          style={{ color: isThin ? '#94A3B8' : (rawShare != null && rawShare >= 25 ? '#15803D' : '#0F172A') }}>
                        {rawShare != null
                          ? <span title={isThin ? `Only ${peersWithData} other home(s) with obit data within ${localRadius}mi — share is unreliable. Add more competitors or widen radius.` : ''}>
                              {rawShare.toFixed(1)}%{isThin && <span style={{ marginLeft: 4 }}>⚠</span>}
                            </span>
                          : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-[11px]" style={{ color: peersTotal > 0 && peersWithData / Math.max(1, peersTotal) < 0.5 ? '#A16207' : '#64748B' }}>
                        {local ? `${peersWithData} / ${peersTotal} peers` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: '#94A3B8' }}>
          Counts from each home's website sitemap, filtered to the selected scope.
          <strong style={{ color: '#475569' }}> Local share</strong> is each home's % within {localRadius} miles of itself — i.e. its share of the regional market it actually competes in, not the global total.
          <br/>
          <strong style={{ color: '#475569' }}>Coverage</strong> shows peers-with-data / total peers in the radius. A home with very few peers (⚠) has an unreliable share — the missing competitors aren't in our DB or haven't been scraped yet. Add more competitors via Discover or widen the radius.
          <br/>
          ⚠ on Obits = sitemap shows 1000+ entries in this window. For a shared chain feed that's real combined volume (number is a floor); for a solo home it's almost always a CMS bulk-touched timestamp, not true volume — no single home in our target market does close to 1000 calls/yr. No annualization, no AI estimates.
        </div>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// TrendsPanel — scope picker + sparkline of historical share %.
// Renders inline SVG (no chart library dependency). Each captured snapshot
// becomes a point; X-axis is captured_at ordered ascending.
// ---------------------------------------------------------------------------
function TrendsPanel({ scope, setScope, data, loading, metric, setMetric, statesPresent, territories }) {
  // Series we plot — share percent (0..100) over time.
  // Metric is obit-based now: ytd / last_12mo / homes (count share)
  const series = (data || []).map(d => {
    let v = null;
    if (metric === 'ytd')       v = Number(d.lfg_obit_share_pct_ytd);
    else if (metric === 'homes') v = Number(d.lfg_home_share_pct);
    else                         v = Number(d.lfg_obit_share_pct_last_12mo);
    return { t: new Date(d.captured_at), v, raw: d };
  }).filter(p => p.v != null && !Number.isNaN(p.v));

  const lastPoint = series[series.length - 1];
  const firstPoint = series[0];
  const delta = (lastPoint && firstPoint) ? (lastPoint.v - firstPoint.v) : null;

  return (
    <SectionCard
      title="Trends"
      count={series.length}
      icon={TrendingUp}
      accent="#0EA5E9"
      action={(
        <div className="flex items-center gap-2 flex-wrap">
          {/* Metric toggle */}
          <div className="inline-flex rounded-md overflow-hidden" style={{ border: '1px solid #E5E9F0' }}>
            {[
              { id: 'ytd',       label: 'Obits YTD' },
              { id: 'last_12mo', label: 'Obits 12mo' },
              { id: 'homes',     label: 'Home count' },
            ].map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMetric(m.id)}
                className="h-7 px-2.5 text-[11px] font-semibold uppercase tracking-wider"
                style={metric === m.id
                  ? { backgroundColor: '#1A3665', color: 'white' }
                  : { backgroundColor: 'white', color: '#475569' }}
              >
                {m.label}
              </button>
            ))}
          </div>
          {/* Scope picker */}
          <select
            value={`${scope.type}:${scope.value || ''}`}
            onChange={e => {
              const [type, value] = e.target.value.split(/:(.+)/);
              if (type === 'overall') setScope({ type: 'overall', value: null, label: 'Overall' });
              else if (type === 'state') setScope({ type: 'state', value, label: value });
              else if (type === 'region') {
                const t = territories.find(x => x.id === value);
                setScope({ type: 'region', value, label: t?.name || 'Region' });
              }
            }}
            className="h-7 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none"
            style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
          >
            <option value="overall:">Overall</option>
            {statesPresent.length > 0 && (
              <optgroup label="By state">
                {statesPresent.map(s => <option key={s} value={`state:${s}`}>{s}</option>)}
              </optgroup>
            )}
            {territories.length > 0 && (
              <optgroup label="By region">
                {territories.map(t => <option key={t.id} value={`region:${t.id}`}>{t.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
      )}
    >
      <div className="px-5 pb-5">
        {loading ? (
          <div className="py-8 text-center text-[13px]" style={{ color: '#64748B' }}>
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" style={{ color: '#0EA5E9' }} />
            Loading trend…
          </div>
        ) : series.length === 0 ? (
          <div className="py-8 text-center text-[13px]" style={{ color: '#94A3B8' }}>
            No snapshots yet for <span className="font-semibold" style={{ color: '#475569' }}>{scope.label}</span>. Click <span className="font-semibold" style={{ color: '#475569' }}>Capture snapshot</span> to record today's share.
          </div>
        ) : (
          <>
            {/* Header summary */}
            <div className="flex items-baseline gap-3 mb-3 flex-wrap">
              <div className="text-2xl font-semibold tabular-nums" style={{ color: '#0F172A', letterSpacing: '-0.02em' }}>
                {lastPoint.v.toFixed(1)}%
              </div>
              {delta != null && series.length > 1 && (
                <div
                  className="text-[12px] font-semibold tabular-nums px-2 py-0.5 rounded"
                  style={delta >= 0
                    ? { backgroundColor: '#DCFCE7', color: '#15803D' }
                    : { backgroundColor: '#FEE2E2', color: '#B91C1C' }}
                >
                  {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)} pts vs first snapshot
                </div>
              )}
              <div className="text-[11px]" style={{ color: '#94A3B8' }}>
                {metric === 'ytd' ? 'LFG share of YTD obits'
                  : metric === 'last_12mo' ? 'LFG share of last-12mo obits'
                  : 'LFG share of home count'}
                {' · last captured '}
                {lastPoint.t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
            <Sparkline series={series} />
            {/* Recent snapshots list */}
            <div className="mt-4 max-h-48 overflow-y-auto" style={{ border: '1px solid #F0F2F6', borderRadius: 8 }}>
              <table className="w-full text-[12px]">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#FAFBFD' }}>
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Captured</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>LFG</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Comp</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>
                      {metric === 'ytd' ? 'YTD share' : metric === 'last_12mo' ? '12mo share' : 'Home share'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...series].reverse().map((p, i) => {
                    const lfgVal = metric === 'ytd' ? p.raw.lfg_obits_ytd
                      : metric === 'last_12mo' ? p.raw.lfg_obits_last_12mo
                      : p.raw.lfg_homes;
                    const compVal = metric === 'ytd' ? p.raw.competitor_obits_ytd
                      : metric === 'last_12mo' ? p.raw.competitor_obits_last_12mo
                      : p.raw.competitor_homes;
                    return (
                      <tr key={i} style={{ borderTop: '1px solid #F0F2F6' }}>
                        <td className="px-3 py-1.5 tabular-nums" style={{ color: '#475569' }}>
                          {p.t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#0F172A' }}>{lfgVal != null ? lfgVal.toLocaleString() : '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#475569' }}>{compVal != null ? compVal.toLocaleString() : '—'}</td>
                        <td className="px-3 py-1.5 text-right"><SharePill pct={p.v} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}

// Tiny inline-SVG sparkline. No chart-library dependency. Hover dots show
// the captured date + value.
function Sparkline({ series }) {
  const W = 720, H = 120, padX = 6, padY = 12;
  if (!series || series.length === 0) return null;
  const min = Math.min(...series.map(p => p.v), 0);
  const max = Math.max(...series.map(p => p.v), 100);
  const range = Math.max(max - min, 1);
  const xFor = (i) => series.length === 1 ? W / 2 : padX + (i * (W - 2 * padX)) / (series.length - 1);
  const yFor = (v) => padY + (1 - (v - min) / range) * (H - 2 * padY);
  const path = series.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(1)},${yFor(p.v).toFixed(1)}`).join(' ');
  const areaPath = `${path} L${xFor(series.length - 1).toFixed(1)},${(H - padY).toFixed(1)} L${xFor(0).toFixed(1)},${(H - padY).toFixed(1)} Z`;
  return (
    <div className="w-full overflow-hidden rounded-lg" style={{ backgroundColor: '#FAFBFD', border: '1px solid #F0F2F6' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: 140 }}>
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Faint horizontal gridlines at 25/50/75 */}
        {[25, 50, 75].map(g => {
          const y = yFor(g);
          if (y < padY || y > H - padY) return null;
          return (
            <g key={g}>
              <line x1={padX} y1={y} x2={W - padX} y2={y} stroke="#E5E9F0" strokeDasharray="2 3" />
              <text x={W - padX} y={y - 2} textAnchor="end" fontSize="9" fill="#94A3B8">{g}%</text>
            </g>
          );
        })}
        <path d={areaPath} fill="url(#spark-fill)" />
        <path d={path} fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {series.map((p, i) => (
          <g key={i}>
            <circle cx={xFor(i)} cy={yFor(p.v)} r="3" fill="#0EA5E9" stroke="white" strokeWidth="1.5">
              <title>{p.t.toLocaleDateString()}: {p.v.toFixed(1)}%</title>
            </circle>
          </g>
        ))}
      </svg>
    </div>
  );
}

// AI competitor discovery modal — calls discover-competitors edge function.
// Lets the admin pick a state + optionally a county scope. State-only is
// faster but capped at 50 results; per-county is exhaustive.
function DiscoverCompetitorsModal({ organization, onClose, onComplete }) {
  // Mode: which kind of region scope to use.
  //   'radius'    — one of the hard-coded city-radius regions
  //   'territory' — a user-defined territory (states[] / counties[] / polygon)
  //   'state'     — fall-back: whole US state
  //   'county'    — fall-back: single county by name+FIPS
  const [mode, setMode] = useState('radius');
  const [radiusRegionIds, setRadiusRegionIds] = useState([HARDCODED_RADIUS_REGIONS[0].id]);
  const [territories, setTerritories] = useState([]);
  const [territoryIds, setTerritoryIds] = useState([]);
  const [state, setState] = useState('OH');
  const [countyFips, setCountyFips] = useState('');
  const [countyName, setCountyName] = useState('');
  const [maxResults, setMaxResults] = useState(100);
  // Thorough sweep: when enabled (only meaningful for radius/territory),
  // the client decomposes the region into counties and runs a separate,
  // exhaustive county scan for each. Slower but ~2x the coverage of a
  // single broad call.
  const [thoroughSweep, setThoroughSweep] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total, label, runningTotal }
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [enqueuedNotice, setEnqueuedNotice] = useState(null);
  // Per-county scan health: { fips: { homes: int, lastRun: { status, completed_at, created_at, error_message } } }
  const [scanStatus, setScanStatus] = useState(null);
  const [showStatusDetail, setShowStatusDetail] = useState(false);
  // Server-side sweep jobs (the new pg_cron-driven queue)
  const [jobs, setJobs] = useState([]);

  // Load org territories for the picker
  useEffect(() => {
    if (!organization?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('territories')
        .select('id, name, states, county_fips')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (!cancelled) {
        setTerritories(data || []);
        if ((data || []).length > 0) setTerritoryIds([data[0].id]);
      }
    })();
    return () => { cancelled = true; };
  }, [organization?.id]);

  // Load per-county scan status: how many competitor_homes rows we have
  // per FIPS + the most recent ai_analyses run for each FIPS. Lets us show
  // gap counties (no homes / failed last run) and offer a one-click resume.
  const loadScanStatus = async () => {
    if (!organization?.id) return;
    const [homesRes, runsRes] = await Promise.all([
      supabase.from('competitor_homes')
        .select('county_fips')
        .eq('organization_id', organization.id)
        .eq('is_active', true),
      supabase.from('ai_analyses')
        .select('input_params, status, completed_at, created_at, error_message')
        .eq('organization_id', organization.id)
        .eq('analysis_type', 'competitor_discovery')
        .order('created_at', { ascending: false })
        .limit(500),
    ]);
    const counts = {};
    (homesRes.data || []).forEach(h => {
      if (h.county_fips) counts[h.county_fips] = (counts[h.county_fips] || 0) + 1;
    });
    const lastRun = {};
    (runsRes.data || []).forEach(r => {
      const fips = r.input_params?.county_fips;
      if (!fips) return;
      if (!lastRun[fips]) lastRun[fips] = r;  // first wins (we ordered desc)
    });
    setScanStatus({ counts, lastRun });
  };

  useEffect(() => { loadScanStatus(); /* eslint-disable-next-line */ }, [organization?.id]);

  // Load + subscribe to discovery sweep jobs. Realtime keeps the active
  // jobs panel live without polling.
  const loadJobs = async () => {
    if (!organization?.id) return;
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from('discovery_sweep_jobs')
      .select('*')
      .eq('organization_id', organization.id)
      .or(`status.in.(queued,running,failed),completed_at.gte.${since}`)
      .order('created_at', { ascending: false })
      .limit(20);
    setJobs(data || []);
  };

  useEffect(() => {
    if (!organization?.id) return;
    loadJobs();
    const channel = supabase
      .channel(`discovery_sweep_jobs:${organization.id}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'discovery_sweep_jobs',
        filter: `organization_id=eq.${organization.id}`,
      }, () => { loadJobs(); loadScanStatus(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
    /* eslint-disable-next-line */
  }, [organization?.id]);

  const enqueueSweep = async (counties, scopeLabel) => {
    if (counties.length === 0) throw new Error('No counties to scan');
    const { data: { user } } = await supabase.auth.getUser();
    const initialStatus = counties.map(() => 'pending');
    const { error } = await supabase.from('discovery_sweep_jobs').insert({
      organization_id: organization.id,
      created_by: user?.id,
      scope_label: scopeLabel,
      max_results: maxResults,
      counties,
      county_status: initialStatus,
    });
    if (error) throw error;
  };

  const cancelJob = async (jobId) => {
    await supabase.rpc('cancel_discovery_job', { p_job_id: jobId });
    loadJobs();
  };

  // Every county across every hard-coded region, deduped by FIPS.
  const ALL_HARDCODED_COUNTIES = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of HARDCODED_RADIUS_REGIONS) {
      for (const c of r.counties) {
        if (seen.has(c.fips)) continue;
        seen.add(c.fips);
        out.push({ ...c, regionName: r.name });
      }
    }
    return out;
  }, []);

  // Gap counties: ones that have either no homes OR the most recent run
  // for them isn't 'complete' (i.e. failed or stuck in 'processing').
  const gapCounties = useMemo(() => {
    if (!scanStatus) return [];
    return ALL_HARDCODED_COUNTIES.filter(c => {
      const homes = scanStatus.counts[c.fips] || 0;
      const run = scanStatus.lastRun[c.fips];
      const goodRun = run && run.status === 'complete';
      return homes === 0 || !goodRun;
    });
  }, [scanStatus, ALL_HARDCODED_COUNTIES]);

  const completedCounties = useMemo(() => {
    if (!scanStatus) return 0;
    return ALL_HARDCODED_COUNTIES.filter(c => {
      const run = scanStatus.lastRun[c.fips];
      return run && run.status === 'complete';
    }).length;
  }, [scanStatus, ALL_HARDCODED_COUNTIES]);

  // Resolve the list of counties a thorough sweep should scan.
  // For radius regions / territories: union counties across every selected
  // entry, dedupe by FIPS so overlapping regions don't double-scan.
  // For state mode: not eligible for thorough sweep (single call).
  const resolveSweepCounties = () => {
    const collected = [];
    if (mode === 'radius') {
      for (const id of radiusRegionIds) {
        const r = HARDCODED_RADIUS_REGIONS.find(x => x.id === id);
        if (r) collected.push(...(r.counties || []));
      }
    } else if (mode === 'territory') {
      for (const id of territoryIds) {
        const t = territories.find(x => x.id === id);
        if (!t) continue;
        for (const fips of (t.county_fips || [])) {
          collected.push({ fips, name: `FIPS ${fips}`, state: t.states?.[0] || '' });
        }
      }
    }
    const seen = new Set();
    return collected.filter(c => {
      if (seen.has(c.fips)) return false;
      seen.add(c.fips);
      return true;
    });
  };

  const toggleArrayValue = (setter, value) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  // Single-call discovery. Always fetches a fresh session via getSession()
  // so a long-running multi-county sweep doesn't fail when the cached
  // access_token expires mid-loop (Supabase JWTs are 1h by default; a
  // thorough sweep over ~17 counties at 30s pacing + 1–3 min/scan can
  // easily exceed that). getSession() returns the current valid session
  // and refreshes automatically if needed.
  const callOnce = async (_unused, payload) => {
    const { data: { session }, error: authErr } = await supabase.auth.getSession();
    if (authErr || !session?.access_token) throw new Error('Not authenticated (session refresh failed)');
    const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/discover-competitors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(payload),
    });
    const body = await r.json();
    if (!r.ok || !body.success) {
      const err = new Error(body.error || `HTTP ${r.status}`);
      err.rate_limited = !!body.rate_limited;
      throw err;
    }
    return body;
  };

  const sleep = (ms) => new Promise(res => setTimeout(res, ms));

  // Per-county serial sweep with 30s pacing + 60s rate-limit retry.
  // Used by both the normal Run-Discovery path and the Resume-Gaps button.
  const sweepCounties = async (session, counties, scopeLabel) => {
    let totalFound = 0;
    let totalInserted = 0;
    let totalUpdated = 0;
    let totalErrors = 0;
    const startTs = Date.now();
    // Inter-county delay to stay under the 450K input-tokens/min Anthropic
    // rate limit. A typical county scan uses ~50–120K tokens; pacing the
    // sweep to ~one county per 30s keeps the rolling-minute total safely
    // below the cap. First county runs immediately.
    const INTER_COUNTY_DELAY_MS = 30_000;
    for (let i = 0; i < counties.length; i++) {
      const c = counties[i];
      if (i > 0) {
        setProgress({
          current: i + 1, total: counties.length,
          label: `Pacing for rate limit (${INTER_COUNTY_DELAY_MS / 1000}s)…`,
          runningTotal: totalFound,
        });
        await sleep(INTER_COUNTY_DELAY_MS);
      }
      setProgress({
        current: i + 1, total: counties.length,
        label: `${c.name}${c.state ? `, ${c.state}` : ''}`,
        runningTotal: totalFound,
      });
      const countyPayload = {
        region_kind: 'county',
        region_label: `${c.name}, ${c.state}`,
        state: c.state,
        county_fips: c.fips,
        county_name: c.name,
        max_results: maxResults,
      };
      let body = null;
      try {
        body = await callOnce(session, countyPayload);
      } catch (e) {
        if (e.rate_limited) {
          setProgress({
            current: i + 1, total: counties.length,
            label: `Rate limited — waiting 60s before retrying ${c.name}…`,
            runningTotal: totalFound,
          });
          await sleep(60_000);
          try {
            body = await callOnce(session, countyPayload);
          } catch (e2) {
            console.warn(`County ${c.name} failed after retry:`, e2.message);
            totalErrors++;
          }
        } else {
          console.warn(`County ${c.name} failed:`, e.message);
          totalErrors++;
        }
      }
      if (body) {
        totalFound += body.total_found || 0;
        totalInserted += body.inserted || 0;
        totalUpdated += body.updated || 0;
        totalErrors += body.errors || 0;
      }
    }
    const elapsed = ((Date.now() - startTs) / 1000).toFixed(1);
    return {
      scope: scopeLabel,
      county_count: counties.length,
      total_found: totalFound, inserted: totalInserted, updated: totalUpdated,
      errors: totalErrors, elapsed_seconds: parseFloat(elapsed), thorough: true,
    };
  };

  const runGapResume = async () => {
    setError(null);
    setEnqueuedNotice(null);
    setRunning(true);
    try {
      if (gapCounties.length === 0) {
        setEnqueuedNotice({ kind: 'info', text: 'No gaps to resume.' });
        return;
      }
      await enqueueSweep(gapCounties, `Gap resume — ${gapCounties.length} counties`);
      setEnqueuedNotice({
        kind: 'success',
        text: `Sweep queued: ${gapCounties.length} counties. Processes 1/min server-side — survives browser close.`,
      });
      await loadJobs();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const run = async () => {
    setError(null);
    setProgress(null);
    setEnqueuedNotice(null);
    setRunning(true);
    try {
      // ---- Thorough sweep path: ENQUEUE a server-side job ----
      const counties = (mode === 'radius' || mode === 'territory') && thoroughSweep
        ? resolveSweepCounties()
        : [];
      if (counties.length > 0) {
        const regionLabel = mode === 'radius'
          ? radiusRegionIds.map(id => HARDCODED_RADIUS_REGIONS.find(x => x.id === id)?.name).filter(Boolean).join(' + ')
          : territoryIds.map(id => territories.find(x => x.id === id)?.name).filter(Boolean).join(' + ');
        await enqueueSweep(counties, regionLabel || 'Region');
        setEnqueuedNotice({
          kind: 'success',
          text: `Sweep queued: ${counties.length} counties. Processes 1/min server-side — survives browser close.`,
        });
        await loadJobs();
        return;
      }

      // ---- Single-call path (synchronous, used for state/county/single-region) ----
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      let payload = { max_results: maxResults };
      if (mode === 'radius') {
        if (radiusRegionIds.length === 0) throw new Error('Pick at least one region');
        if (radiusRegionIds.length > 1) throw new Error('Multiple regions require thorough sweep — enable it above');
        const r = HARDCODED_RADIUS_REGIONS.find(x => x.id === radiusRegionIds[0]);
        if (!r) throw new Error('Pick a region');
        payload = {
          ...payload, region_kind: 'radius', region_label: r.name,
          radius_center_city: r.center, radius_center_lat: r.lat, radius_center_lng: r.lng,
          radius_miles: r.radiusMiles,
        };
      } else if (mode === 'territory') {
        if (territoryIds.length === 0) throw new Error('Pick at least one territory');
        if (territoryIds.length > 1) throw new Error('Multiple territories require thorough sweep — enable it above');
        const t = territories.find(x => x.id === territoryIds[0]);
        payload = { ...payload, region_kind: 'territory', region_label: t?.name, territory_id: territoryIds[0] };
      } else if (mode === 'county') {
        payload = {
          ...payload, region_kind: 'county', state,
          county_fips: countyFips.trim() || null, county_name: countyName.trim() || null,
        };
      } else {
        payload = { ...payload, region_kind: 'state', state };
      }
      const body = await callOnce(session, payload);
      setResult(body);
      await loadScanStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  const close = () => {
    if (result) onComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={close}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #F0F2F6' }}>
          <h3 className="font-semibold text-base inline-flex items-center gap-2" style={{ color: '#7C3AED' }}>
            <Sparkles className="w-4 h-4" /> Discover competitors
          </h3>
          <button type="button" onClick={close} disabled={running} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!result ? (
          <>
            <div className="px-5 py-4 space-y-3 max-h-[75vh] overflow-y-auto">
              <p className="text-[13px]" style={{ color: '#475569' }}>
                Claude web-searches state funeral-board rosters, NFDA, Google, and Yelp to enumerate every active funeral home in the chosen scope. Idempotent: existing rows (matched on name + city) get updated, new ones inserted.
              </p>

              {/* Scan health: shows what's been completed across the hard-coded regions */}
              {scanStatus && (
                <div className="rounded-lg p-3" style={{ backgroundColor: gapCounties.length > 0 ? '#FFFBEB' : '#ECFDF5', border: `1px solid ${gapCounties.length > 0 ? '#FDE68A' : '#A7F3D0'}` }}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[12px] font-semibold" style={{ color: gapCounties.length > 0 ? '#92400E' : '#047857' }}>
                      {gapCounties.length > 0
                        ? `${gapCounties.length} of ${ALL_HARDCODED_COUNTIES.length} core LFG counties have gaps`
                        : `All ${ALL_HARDCODED_COUNTIES.length} core LFG counties scanned`}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowStatusDetail(s => !s)}
                        className="text-[11px] underline"
                        style={{ color: gapCounties.length > 0 ? '#92400E' : '#047857' }}
                      >
                        {showStatusDetail ? 'Hide' : 'Detail'}
                      </button>
                      {gapCounties.length > 0 && (
                        <button
                          type="button"
                          onClick={runGapResume}
                          disabled={running}
                          className="px-2 py-1 text-[11px] font-semibold text-white rounded disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)' }}
                        >
                          Resume gaps ({gapCounties.length})
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: gapCounties.length > 0 ? '#B45309' : '#059669' }}>
                    {completedCounties} completed · {gapCounties.length} missing/failed · estimated {Math.ceil(gapCounties.length * 0.7)}–{Math.ceil(gapCounties.length * 1.4)} min for resume
                  </div>
                  {showStatusDetail && (
                    <div className="mt-2 max-h-48 overflow-y-auto rounded" style={{ backgroundColor: 'white', border: '1px solid #E5E9F0' }}>
                      <table className="w-full text-[11px]">
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#FAFBFD' }}>
                          <tr>
                            <th className="text-left px-2 py-1 font-semibold" style={{ color: '#64748B' }}>County</th>
                            <th className="text-left px-2 py-1 font-semibold" style={{ color: '#64748B' }}>Region</th>
                            <th className="text-right px-2 py-1 font-semibold" style={{ color: '#64748B' }}>Homes</th>
                            <th className="text-left px-2 py-1 font-semibold" style={{ color: '#64748B' }}>Last run</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ALL_HARDCODED_COUNTIES.map(c => {
                            const homes = scanStatus.counts[c.fips] || 0;
                            const run = scanStatus.lastRun[c.fips];
                            const ok = run && run.status === 'complete';
                            const dotColor = ok && homes > 0 ? '#10B981' : (run && run.status === 'failed') ? '#EF4444' : '#F59E0B';
                            const ts = run?.completed_at || run?.created_at;
                            return (
                              <tr key={c.fips} style={{ borderTop: '1px solid #F0F2F6' }}>
                                <td className="px-2 py-1" style={{ color: '#0F172A' }}>
                                  <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ backgroundColor: dotColor }} />
                                  {c.name}, {c.state}
                                </td>
                                <td className="px-2 py-1" style={{ color: '#64748B' }}>{c.regionName}</td>
                                <td className="px-2 py-1 text-right tabular-nums" style={{ color: '#0F172A' }}>{homes}</td>
                                <td className="px-2 py-1" style={{ color: '#64748B' }}>
                                  {run ? `${run.status}${ts ? ` · ${new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}` : 'never'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Active sweep jobs — server-side queue, survives browser close */}
              {jobs.length > 0 && (
                <div className="rounded-lg p-3 space-y-2" style={{ backgroundColor: '#EFF4FB', border: '1px solid #DBE6F5' }}>
                  <div className="text-[12px] font-semibold" style={{ color: '#1A3665' }}>
                    Sweep jobs (last 24h)
                  </div>
                  {jobs.map(j => {
                    const total = (j.counties || []).length;
                    const done = j.current_index || 0;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                    const isActive = j.status === 'queued' || j.status === 'running';
                    const statusColor = {
                      queued: '#94A3B8', running: '#1A3665',
                      complete: '#15803D', failed: '#B91C1C', cancelled: '#64748B',
                    }[j.status] || '#64748B';
                    const t = j.totals || {};
                    return (
                      <div key={j.id} className="rounded p-2" style={{ backgroundColor: 'white', border: '1px solid #E5E9F0' }}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[12px] font-semibold truncate" style={{ color: '#0F172A' }} title={j.scope_label}>
                            {j.scope_label || 'Sweep'}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: statusColor }}>
                              {j.status}
                            </span>
                            {isActive && (
                              <button
                                type="button"
                                onClick={() => cancelJob(j.id)}
                                className="text-[10px] underline"
                                style={{ color: '#B91C1C' }}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="text-[11px] mt-0.5 tabular-nums" style={{ color: '#475569' }}>
                          {done}/{total} counties · {t.found || 0} found · {t.inserted || 0} new / {t.updated || 0} updated
                          {t.cost_usd > 0 && <span> · ~${t.cost_usd.toFixed(2)}</span>}
                          {t.errors > 0 && <span style={{ color: '#B91C1C' }}> · {t.errors} errors</span>}
                        </div>
                        {isActive && (
                          <div className="h-1 rounded-full overflow-hidden mt-1.5" style={{ backgroundColor: '#DBE6F5' }}>
                            <div className="h-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #1A3665, #2E4B7C)' }} />
                          </div>
                        )}
                        {j.error_message && (
                          <div className="text-[11px] mt-1 truncate" style={{ color: '#B91C1C' }} title={j.error_message}>
                            {j.error_message}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Mode picker — region-scoped is the primary path */}
              <div className="grid grid-cols-4 gap-1 rounded-lg overflow-hidden" style={{ border: '1px solid #DDD6FE' }}>
                {[
                  { id: 'radius',    label: 'Region' },
                  { id: 'territory', label: 'Territory' },
                  { id: 'county',    label: 'County' },
                  { id: 'state',     label: 'State' },
                ].map(opt => {
                  const active = mode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setMode(opt.id)}
                      disabled={running || (opt.id === 'territory' && territories.length === 0)}
                      className="h-8 text-[12px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      style={active
                        ? { background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)', color: 'white' }
                        : { backgroundColor: 'white', color: '#7C3AED' }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {mode === 'radius' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                      Regions ({radiusRegionIds.length} selected)
                    </label>
                    <div className="flex items-center gap-2 text-[10px]">
                      <button type="button" disabled={running} onClick={() => setRadiusRegionIds(HARDCODED_RADIUS_REGIONS.map(r => r.id))}
                        className="font-semibold underline" style={{ color: '#7C3AED' }}>All</button>
                      <button type="button" disabled={running} onClick={() => setRadiusRegionIds([])}
                        className="font-semibold underline" style={{ color: '#94A3B8' }}>None</button>
                    </div>
                  </div>
                  <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #E5E9F0' }}>
                    {HARDCODED_RADIUS_REGIONS.map((r, idx) => {
                      const checked = radiusRegionIds.includes(r.id);
                      return (
                        <label
                          key={r.id}
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 text-sm"
                          style={{ borderTop: idx > 0 ? '1px solid #F0F2F6' : 'none' }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={running}
                            onChange={() => toggleArrayValue(setRadiusRegionIds, r.id)}
                            className="rounded accent-[#7C3AED]"
                          />
                          <span className="flex-1" style={{ color: '#0F172A' }}>{r.name}</span>
                          <span className="text-[11px] tabular-nums" style={{ color: '#94A3B8' }}>{r.counties.length} counties</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {mode === 'territory' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                      Territories ({territoryIds.length} selected)
                    </label>
                    {territories.length > 0 && (
                      <div className="flex items-center gap-2 text-[10px]">
                        <button type="button" disabled={running} onClick={() => setTerritoryIds(territories.map(t => t.id))}
                          className="font-semibold underline" style={{ color: '#7C3AED' }}>All</button>
                        <button type="button" disabled={running} onClick={() => setTerritoryIds([])}
                          className="font-semibold underline" style={{ color: '#94A3B8' }}>None</button>
                      </div>
                    )}
                  </div>
                  {territories.length === 0 ? (
                    <p className="text-[12px] mt-1" style={{ color: '#94A3B8' }}>
                      No territories defined yet. Create them via Portfolio → Pipeline filter → Edit Territories.
                    </p>
                  ) : (
                    <div className="rounded-lg overflow-hidden max-h-48 overflow-y-auto" style={{ border: '1px solid #E5E9F0' }}>
                      {territories.map((t, idx) => {
                        const checked = territoryIds.includes(t.id);
                        const cs = (t.county_fips || []).length;
                        return (
                          <label
                            key={t.id}
                            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 text-sm"
                            style={{ borderTop: idx > 0 ? '1px solid #F0F2F6' : 'none' }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={running}
                              onChange={() => toggleArrayValue(setTerritoryIds, t.id)}
                              className="rounded accent-[#7C3AED]"
                            />
                            <span className="flex-1" style={{ color: '#0F172A' }}>
                              {t.name}{(t.states || []).length > 0 ? ` (${(t.states || []).join(', ')})` : ''}
                            </span>
                            <span className="text-[11px] tabular-nums" style={{ color: '#94A3B8' }}>
                              {cs > 0 ? `${cs} counties` : 'no counties'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {mode === 'county' && (
                <>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>State</label>
                    <select
                      value={state} onChange={e => setState(e.target.value)} disabled={running}
                      className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: '#E5E9F0' }}
                    >
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>County name</label>
                      <input
                        type="text" value={countyName} onChange={e => setCountyName(e.target.value)} disabled={running}
                        placeholder="e.g. Cuyahoga"
                        className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: '#E5E9F0' }}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>FIPS (5-digit)</label>
                      <input
                        type="text" value={countyFips} onChange={e => setCountyFips(e.target.value)} disabled={running}
                        placeholder="e.g. 39035"
                        className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: '#E5E9F0' }}
                      />
                    </div>
                  </div>
                </>
              )}

              {mode === 'state' && (
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>State</label>
                  <select
                    value={state} onChange={e => setState(e.target.value)} disabled={running}
                    className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: '#E5E9F0' }}
                  >
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <p className="text-[11px] mt-1" style={{ color: '#94A3B8' }}>
                    Broad sweep — capped by Max results. Use Region or Territory for thorough coverage.
                  </p>
                </div>
              )}

              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>Max results per scan</label>
                <select
                  value={maxResults}
                  onChange={e => setMaxResults(parseInt(e.target.value, 10))}
                  disabled={running}
                  className="w-full mt-1 border rounded px-2 py-1.5 text-sm"
                  style={{ borderColor: '#E5E9F0' }}
                >
                  <option value={25}>25 (quick scan)</option>
                  <option value={50}>50</option>
                  <option value={100}>100 (default)</option>
                  <option value={200}>200 (full sweep)</option>
                  <option value={300}>300</option>
                </select>
              </div>

              {/* Thorough sweep toggle — only meaningful for region/territory */}
              {(mode === 'radius' || mode === 'territory') && (
                <label className="flex items-start gap-2 text-sm cursor-pointer p-3 rounded-lg" style={{ backgroundColor: '#F5F3FF', border: '1px solid #DDD6FE', color: '#5B21B6' }}>
                  <input
                    type="checkbox"
                    checked={thoroughSweep}
                    onChange={e => setThoroughSweep(e.target.checked)}
                    disabled={running}
                    className="mt-0.5 rounded accent-[#7C3AED]"
                  />
                  <span>
                    <span className="font-semibold">Thorough sweep</span> — decompose into county-level scans across every selected {mode === 'radius' ? 'region' : 'territory'}
                    <span className="block text-[11px] mt-0.5" style={{ color: '#7C3AED' }}>
                      {(() => {
                        const counties = resolveSweepCounties();
                        if (counties.length === 0) {
                          return mode === 'territory'
                            ? 'No counties resolved — selected territories have no county_fips set'
                            : 'No regions selected';
                        }
                        const minutes = Math.ceil(counties.length * 0.7);
                        return `${counties.length} unique counties (deduped) — est. ~${minutes}–${minutes * 2} min · ~$${(counties.length * 0.3).toFixed(1)}–${(counties.length * 0.6).toFixed(1)}`;
                      })()}
                    </span>
                  </span>
                </label>
              )}

              <p className="text-[11px]" style={{ color: '#94A3B8' }}>
                Each county runs a two-pass Claude scan (~50–120K tokens) with 30s pacing between counties to stay under the 450K tokens/min API rate limit.
              </p>

              {progress && (
                <div className="rounded-lg p-3" style={{ backgroundColor: '#F5F3FF', border: '1px solid #DDD6FE' }}>
                  <div className="text-[12px] font-semibold mb-1" style={{ color: '#5B21B6' }}>
                    Scanning county {progress.current} of {progress.total}: {progress.label}
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#DDD6FE' }}>
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${Math.round((progress.current / progress.total) * 100)}%`,
                        background: 'linear-gradient(90deg, #7C3AED, #A78BFA)',
                      }}
                    />
                  </div>
                  <div className="text-[11px] mt-1.5 tabular-nums" style={{ color: '#7C3AED' }}>
                    {progress.runningTotal} homes found across completed counties
                  </div>
                </div>
              )}

              {enqueuedNotice && (
                <div
                  className="rounded-lg px-3 py-2 text-[12px]"
                  style={{
                    backgroundColor: enqueuedNotice.kind === 'success' ? '#ECFDF5' : '#FEF3C7',
                    border: `1px solid ${enqueuedNotice.kind === 'success' ? '#A7F3D0' : '#FDE68A'}`,
                    color: enqueuedNotice.kind === 'success' ? '#047857' : '#92400E',
                  }}
                >
                  {enqueuedNotice.text}
                </div>
              )}

              {error && <div className="text-xs" style={{ color: '#B91C1C' }}>{error}</div>}
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid #F0F2F6' }}>
              <button type="button" onClick={close} disabled={running} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Close</button>
              <button
                type="button"
                onClick={run}
                disabled={running}
                className="px-3 py-1.5 text-sm font-semibold text-white rounded disabled:opacity-50 inline-flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)' }}
              >
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {running ? 'Working…' : ((mode === 'radius' || mode === 'territory') && thoroughSweep ? 'Queue sweep' : 'Run discovery')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="px-5 py-4 space-y-2 text-sm">
              <div className="font-semibold" style={{ color: '#0F172A' }}>{result.scope}</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded p-2" style={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                  <div className="text-[10px] uppercase font-semibold" style={{ color: '#047857' }}>Found</div>
                  <div className="text-xl tabular-nums" style={{ color: '#047857' }}>{result.total_found}</div>
                </div>
                <div className="rounded p-2" style={{ backgroundColor: '#EFF4FB', border: '1px solid #DBE6F5' }}>
                  <div className="text-[10px] uppercase font-semibold" style={{ color: '#1A3665' }}>Inserted / Updated</div>
                  <div className="text-xl tabular-nums" style={{ color: '#1A3665' }}>
                    {result.inserted} / {result.updated}
                  </div>
                </div>
              </div>
              {result.errors > 0 && (
                <div className="text-xs" style={{ color: '#B91C1C' }}>
                  {result.errors} row(s) failed to save — see ai_analyses run for details.
                </div>
              )}
              <div className="text-[11px]" style={{ color: '#94A3B8' }}>
                Completed in {result.elapsed_seconds}s
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end" style={{ borderTop: '1px solid #F0F2F6' }}>
              <button
                type="button"
                onClick={close}
                className="px-3 py-1.5 text-sm font-semibold text-white rounded inline-flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #1A3665 0%, #2E4B7C 100%)' }}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AddCompetitorModal({ organization, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', address: '', city: '', state: '', zip: '',
    county_fips: '', county_name: '', phone: '', website: '',
    parent_company: '', is_consolidator: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setError(null);
    if (!form.name.trim()) { setError('Name required'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from('competitor_homes').insert({
        organization_id: organization.id,
        name: form.name.trim(),
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim().toUpperCase().slice(0, 2) || null,
        zip: form.zip.trim() || null,
        county_fips: form.county_fips.trim() || null,
        county_name: form.county_name.trim() || null,
        phone: form.phone.trim() || null,
        website: form.website.trim() || null,
        parent_company: form.parent_company.trim() || null,
        is_consolidator: !!form.is_consolidator,
        source: 'manual',
      });
      if (error) throw error;
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #F0F2F6' }}>
          <h3 className="font-semibold text-base" style={{ color: '#1A3665' }}>Add competitor home</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-3">
          {[
            ['name', 'Name *'],
            ['address', 'Address'],
            ['city', 'City'],
            ['state', 'State (2-letter)'],
            ['zip', 'ZIP'],
            ['county_fips', 'County FIPS (5-digit)'],
            ['county_name', 'County name'],
            ['phone', 'Phone'],
            ['website', 'Website'],
            ['parent_company', 'Parent company / chain'],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="text-[11px] font-semibold text-gray-600 block mb-0.5">{label}</label>
              <input
                type="text"
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                disabled={saving}
                className="w-full border rounded px-2 py-1.5 text-sm"
                style={{ borderColor: '#E5E9F0' }}
              />
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: '#334155' }}>
            <input
              type="checkbox"
              checked={form.is_consolidator}
              onChange={e => setForm(f => ({ ...f, is_consolidator: e.target.checked }))}
              className="rounded accent-[#1A3665]"
            />
            Consolidator chain (SCI, Carriage, etc.)
          </label>
          {error && <div className="text-xs" style={{ color: '#B91C1C' }}>{error}</div>}
        </div>
        <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid #F0F2F6' }}>
          <button type="button" onClick={onClose} disabled={saving} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !form.name.trim()}
            className="px-3 py-1.5 text-sm font-semibold text-white rounded disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{ background: 'linear-gradient(135deg, #1A3665 0%, #2E4B7C 100%)' }}
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save competitor
          </button>
        </div>
      </div>
    </div>
  );
}
